import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Tooltip,
  Divider,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { Search as SearchIcon, KeyboardArrowDown, KeyboardArrowRight, KeyboardArrowUp, ExpandLess, ExpandMore, OpenInNew, InfoOutlined as InfoIcon } from '@mui/icons-material';
import { DataGrid, GridColDef, GridSortModel, GridColumnGroupingModel } from '@mui/x-data-grid';
import { Link as RouterLink, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../api/axios';
import { AcuityExpandPanel } from '../components/understaffing';

/* ───────────────── state names ───────────────── */
const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia', PR: 'Puerto Rico', GU: 'Guam', VI: 'Virgin Islands',
};

/* ───────────────── types ───────────────── */
interface ChainRow {
  id: string;
  name: string;
  state: string;
  owner_id: number | null;
  effective_owner_id: string;
  primary_affiliation: string | null;
  num_facilities: number;
  ccn_list: string[];
  dba_name?: string;
  total_understaffed_hprd_hrs: number | null;
  total_understaffed_hprd_cost: number | null;
  pct_days_below_cms_hprd: number | null;
  total_citations: number | null;
  total_citations_per_100: number | null;
  total_harm_citations: number | null;
  total_staffing_ftags: number | null;
  total_fine_amount: number | null;
  avg_overall_rating: number | null;
  avg_staffing_rating: number | null;
  metadata: Record<string, any>;
  state_percentile_ranks?: Record<string, number | null>;
}

interface SummaryData {
  total_chains: number;
  total_facilities: number;
  total_facility_years: number;
  states: string[];
  years: number[];
  aggregates: {
    total_understaffed_hprd_hrs: number | null;
    total_understaffed_hprd_cost: number | null;
    total_harm_citations: number | null;
    total_fine_amount: number | null;
  };
}

interface ProsecutionBaseline {
  facilities: number;
  nursing_hrs_per_patient_day: number | null;  // Key HPRD metric
  pct_days_below_cms_hprd: number | null;
  total_citations_per_100: number | null;
  harm_per_100_residents: number | null;
  avg_overall_rating: number | null;
  avg_staffing_rating: number | null;
  understaffed_state_min_hprd_hrs_per_res_day: number | null;
  ant_pct_acuity_staffed_total: number | null;
  ant_pct_acuity_staffed_rn: number | null;
  ant_pct_acuity_staffed_lpn: number | null;
  ant_pct_acuity_staffed_cna: number | null;
}

// Legacy alias for compatibility
type SweetwaterBaseline = ProsecutionBaseline;

// California state minimum requirement (for reference only)
const CA_STATE_MIN_HPRD = 3.5;

// Fallback values if API fails (should rarely be needed)
// These are calculated from PBJ data (RN + LPN + CNA only)
const FALLBACK_sweetwaterHprd = 3.52;
const FALLBACK_DEPTFORD_HPRD = 3.17;

/* ───────────────── helpers ───────────────── */
const formatMoney = (val: number | null | undefined): string => {
  if (val == null) return '-';
  const prefix = val < 0 ? '-$' : '$';
  return `${prefix}${Math.abs(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatNumber = (val: number | null | undefined, decimals = 0): string => {
  if (val == null) return '-';
  return val.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatHours = (val: number | null | undefined): string => {
  if (val == null) return '-';
  const absVal = Math.abs(val);
  if (absVal >= 1000000) {
    return `${(absVal / 1000000).toFixed(1)}M hrs`;
  }
  if (absVal >= 1000) {
    return `${(absVal / 1000).toFixed(1)}K hrs`;
  }
  return `${absVal.toFixed(0)} hrs`;
};

const formatPercent = (val: number | null | undefined): string => {
  if (val == null) return '-';
  return `${val.toFixed(1)}%`;
};

const getRatingColor = (rating: number | null): string => {
  if (rating == null) return 'default';
  if (rating <= 1.5) return 'error';
  if (rating <= 2.5) return 'warning';
  if (rating <= 3.5) return 'info';
  return 'success';
};

// Strip trailing state suffix like " (CA)" from chain names
const stripStateFromName = (name: string): string => {
  return name.replace(/\s*\([A-Z]{2}\)\s*$/, '');
};

// Format percentile rank with ordinal suffix (e.g., "92nd")
const formatPercentileRank = (percentile: number | null | undefined): string | null => {
  if (percentile == null) return null;
  const rounded = Math.round(percentile);
  const suffix = getOrdinalSuffix(rounded);
  return `${rounded}${suffix}`;
};

// Get ordinal suffix for a number (1st, 2nd, 3rd, 4th, etc.)
const getOrdinalSuffix = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

// Sweetwater comparison indicator - shows how a value compares to Sweetwater baseline
// For metrics where higher is worse (understaffing, citations), we show worse/better accordingly
const SweetwaterComparison = ({
  value,
  baseline,
  higherIsWorse = true,
  format = 'number',
  decimals = 1,
}: {
  value: number | null | undefined;
  baseline: number | null | undefined;
  higherIsWorse?: boolean;
  format?: 'number' | 'percent';
  decimals?: number;
}) => {
  if (value == null || baseline == null) return null;

  const diff = value - baseline;
  const pctDiff = baseline !== 0 ? (diff / baseline) * 100 : 0;

  // Determine if this is worse or better than Sweetwater
  const isWorse = higherIsWorse ? diff > 0 : diff < 0;
  const isBetter = higherIsWorse ? diff < 0 : diff > 0;
  const isSimilar = Math.abs(pctDiff) < 5; // Within 5% is "similar"

  let color = 'text.secondary';
  let symbol = '';
  if (!isSimilar) {
    if (isWorse) {
      color = 'error.main';
      symbol = '\u25B2'; // Up arrow
    } else if (isBetter) {
      color = 'success.main';
      symbol = '\u25BC'; // Down arrow
    }
  }

  const displayDiff = format === 'percent'
    ? `${diff > 0 ? '+' : ''}${diff.toFixed(decimals)}pp`
    : `${diff > 0 ? '+' : ''}${diff.toFixed(decimals)}`;

  return (
    <Tooltip title={`Sweetwater baseline: ${format === 'percent' ? baseline.toFixed(decimals) + '%' : baseline.toFixed(decimals)} (${pctDiff > 0 ? '+' : ''}${pctDiff.toFixed(0)}%)`}>
      <Typography
        component="span"
        variant="caption"
        sx={{ color, ml: 0.5, fontWeight: 500, fontSize: '0.7rem' }}
      >
        {symbol} {displayDiff}
      </Typography>
    </Tooltip>
  );
};

// HPRD Baseline Comparison - compares facility's actual HPRD to prosecution baselines
// For HPRD, LOWER is worse (less care provided)
const HPRDBaselineComparison = ({
  value,
  baselineName,
  baselineValue,
}: {
  value: number | null | undefined;
  baselineName: 'Sweetwater' | 'NJ';
  baselineValue: number;
}) => {
  if (value == null) return null;

  const diff = value - baselineValue;
  const pctDiff = (diff / baselineValue) * 100;

  // For HPRD, lower is worse (less care)
  const isWorse = diff < 0;
  const isMuchWorse = pctDiff < -10;

  let color = 'text.secondary';
  let bgColor = 'transparent';
  if (isMuchWorse) {
    color = 'error.main';
    bgColor = 'error.50';
  } else if (isWorse) {
    color = 'warning.dark';
  }

  const label = isWorse
    ? `${Math.abs(pctDiff).toFixed(0)}% below ${baselineName}`
    : `${pctDiff.toFixed(0)}% above ${baselineName}`;

  return (
    <Tooltip title={`${baselineName} baseline: ${baselineValue.toFixed(2)} hrs/patient/day. This facility: ${value.toFixed(2)} (${diff > 0 ? '+' : ''}${diff.toFixed(2)} hrs)`}>
      <Chip
        label={label}
        size="small"
        sx={{
          height: 20,
          fontSize: '0.7rem',
          fontWeight: 600,
          color,
          backgroundColor: bgColor,
          border: isWorse ? '1px solid' : 'none',
          borderColor: isMuchWorse ? 'error.main' : 'warning.main',
          '& .MuiChip-label': { px: 1 },
        }}
      />
    </Tooltip>
  );
};

const AcuityBaselineComparison = ({
  value,
  baselineName,
  baselineValue,
  metricLabel,
}: {
  value: number | null | undefined;
  baselineName: 'Sweetwater' | 'Deptford';
  baselineValue: number;
  metricLabel: string;
}) => {
  if (value == null) return null;

  // Both value and baselineValue are deficit form (negative = understaffed)
  const deficit = value - 100;
  const diff = deficit - baselineValue;

  // More negative deficit = worse
  const isWorse = diff < 0;
  const isMuchWorse = diff < -10;

  let color = 'text.secondary';
  let bgColor = 'transparent';
  if (isMuchWorse) {
    color = 'error.main';
    bgColor = 'error.50';
  } else if (isWorse) {
    color = 'warning.dark';
  }

  const label = isWorse
    ? `▼ ${Math.abs(diff).toFixed(1)}% worse`
    : `▲ ${diff.toFixed(1)}% better`;

  return (
    <Tooltip title={<>{baselineName}: {baselineValue.toFixed(1)}%<br />This facility: {deficit.toFixed(1)}% ({diff > 0 ? '+' : ''}{diff.toFixed(1)}%)</>}>
      <Chip
        label={label}
        size="small"
        sx={{
          height: 20,
          fontSize: '0.7rem',
          fontWeight: 600,
          color,
          backgroundColor: bgColor,
          border: isWorse ? '1px solid' : 'none',
          borderColor: isMuchWorse ? 'error.main' : 'warning.main',
          '& .MuiChip-label': { px: 1 },
        }}
      />
    </Tooltip>
  );
};

// State staffing requirements — used for the modal dialog and state minimum column
// Only states with numeric HPRD minimums are included. States not listed have no numeric floor.
interface StateStaffingReqs {
  hprd: number;
  cna?: number;
  rnHoursPerDay?: number;
  rnHppd?: number;
  licensedNurseHppd?: number;
  notes?: string[];  // Additional rules/context shown in the modal
  citation: string;  // Legal citation for the PBJ/minimum staffing panel
  shiftRequirements?: { shift: string; requirement: string }[];  // e.g., Morning: max 8 patients per nurse
  acuityText?: React.ReactNode;  // State-specific acuity sufficiency language
  acuityDetail?: React.ReactNode;  // Additional italic detail text
  acuityCitation?: string;  // Citation for acuity panel (defaults to federal)
}

const STATE_STAFFING_REQS: Record<string, StateStaffingReqs> = {
  DC: { hprd: 4.1, citation: '' },
  RI: { hprd: 3.81, citation: '' },
  DE: { hprd: 3.67, citation: '' },
  FL: { hprd: 3.6, cna: 2.0, licensedNurseHppd: 1.0, citation: '' },
  MA: { hprd: 3.58, citation: '' },
  CA: { hprd: 3.5, cna: 2.4, citation: 'Cal. Health & Safety Code § 1276.65; Cal. Code Regs. tit. 22, § 72327' },
  NY: { hprd: 3.5, cna: 2.2, licensedNurseHppd: 1.1, citation: '' },
  WA: { hprd: 3.4, citation: '' },
  PA: { hprd: 3.2, citation: '' },
  CT: { hprd: 3.0, citation: '' },
  MD: { hprd: 3.0, citation: '' },
  VT: { hprd: 3.0, cna: 2.0, citation: '' },
  OK: { hprd: 2.9, citation: '' },
  MS: { hprd: 2.8, citation: '' },
  IL: { hprd: 2.5, citation: '' },
  OH: { hprd: 2.5, citation: '' },
  ID: { hprd: 2.4, citation: '' },
  LA: { hprd: 2.35, citation: '' },
  MI: {
    hprd: 2.25, rnHoursPerDay: 8,
    citation: 'Mich. Comp. Laws § 333.21720a(1)–(2); Mich. Admin. Code R. 325.20703(7).',
    shiftRequirements: [
      { shift: 'Morning', requirement: 'max 8 patients per nurse' },
      { shift: 'Afternoon', requirement: 'max 12 patients per nurse' },
      { shift: 'Nighttime', requirement: 'max 15 patients per nurse' },
    ],
    acuityText: <>A nursing home shall employ nursing personnel <strong>sufficient to provide continuous 24-hour nursing care and services sufficient to meet the needs of each patient</strong> in the nursing home.</>,
    acuityDetail: <>Additionally, Michigan requires that residents &quot;receive preventive, supportive, maintenance, habilitative, and rehabilitative nursing care directed to the physiologic and psychosocial needs and well-being of that patient.&quot;</>,
    acuityCitation: 'Mich. Comp. Laws § 333.21720a(2); Mich. Admin. Code R. 325.20707(1).',
  },
  WV: { hprd: 2.25, citation: '' },
  CO: { hprd: 2.0, citation: '' },
  GA: { hprd: 2.0, citation: '' },
  MN: { hprd: 2.0, citation: '' },
  TN: { hprd: 2.0, licensedNurseHppd: 0.4, citation: '' },
  WI: { hprd: 2.0, citation: '' },
  KS: { hprd: 1.85, citation: '' },
  WY: { hprd: 1.5, citation: '' },
};

const dataGridStyles = {
  backgroundColor: 'white',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  overflow: 'hidden',
  height: '100%',
  '& .MuiDataGrid-row:hover': {
    cursor: 'pointer',
    backgroundColor: 'action.hover',
  },
  '& .MuiDataGrid-virtualScrollerContent': {
    paddingTop: 0,
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid',
    borderBottomColor: 'divider',
    display: 'flex',
    alignItems: 'center',
    padding: '12px 8px',
    fontSize: '0.9375rem',
  },
  '& .MuiDataGrid-cell:first-of-type': {
    paddingLeft: '24px',
  },
  '& .MuiDataGrid-cell:last-of-type': {
    paddingRight: '24px',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#e0e0e0',
    borderBottom: '3px solid #9e9e9e',
    maxHeight: 'none !important',
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: '#e0e0e0',
    padding: '8px 8px',
    height: 'auto !important',
    maxHeight: 'none !important',
    alignItems: 'center',
    // Remove MUI default right borders on group headers to prevent double borders
    '&.MuiDataGrid-columnHeader--withRightBorder': {
      borderRight: 'none !important',
    },
  },
  '& .MuiDataGrid-cell--withRightBorder': {
    borderRight: 'none !important',
  },
  '& .MuiDataGrid-columnHeader:first-of-type': {
    paddingLeft: '24px',
  },
  '& .MuiDataGrid-columnHeader:last-of-type': {
    paddingRight: '24px',
  },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    overflow: 'visible',
    whiteSpace: 'normal',
    lineHeight: 1.3,
  },
  '& .MuiDataGrid-columnHeaderTitleContainerContent': {
    overflow: 'visible',
    whiteSpace: 'normal',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: 'text.primary',
    fontWeight: 700,
    fontSize: '0.8125rem',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    lineHeight: 1.3,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflow: 'visible',
    textOverflow: 'unset',
  },
  '& .MuiDataGrid-iconButtonContainer': {
    display: 'none',
  },
  '& .MuiDataGrid-sortIcon': {
    display: 'none',
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid',
    borderTopColor: 'divider',
    backgroundColor: '#fafafa',
  },
  '& .detail-panel-row': {
    backgroundColor: '#fafbfc',
    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.04)',
    cursor: 'default !important',
  },
  '& .detail-panel-row:hover': {
    backgroundColor: '#fafbfc !important',
    cursor: 'default !important',
  },
  '& .detail-panel-row .MuiDataGrid-cell': {
    padding: '16px 24px !important',
    borderBottom: '2px solid',
    borderBottomColor: 'divider',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  '& .risk-score-cell': {
    borderLeft: '2px solid',
    borderLeftColor: 'primary.main',
  },
  '& .MuiDataGrid-columnHeader.risk-score-header, & .MuiDataGrid-columnHeader.risk-score-group': {
    borderLeft: '2px solid',
    borderLeftColor: 'primary.main',
  },
  '& .section-divider-cell': {
    borderLeft: '1px solid',
    borderLeftColor: 'divider',
  },
  '& .MuiDataGrid-columnHeader.section-divider-header': {
    borderLeft: '1px solid',
    borderLeftColor: 'divider',
  },
  // Column group header row — thin model label row
  '& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer': {
    justifyContent: 'center',
  },
  '& .MuiDataGrid-columnHeader--filledGroup:not(.risk-score-group)': {
    paddingTop: '10px !important',
    paddingBottom: '0px !important',
    alignItems: 'flex-end !important',
  },
  // Section dividers on group row + column row
  '& .MuiDataGrid-columnHeader.group-divider-left': {
    borderLeft: '1px solid',
    borderLeftColor: 'divider',
  },
  '& .mds-cost-cell': {},
  // Grouped column tints
  '& .total-acuity-group': {
    backgroundColor: 'rgba(21, 101, 192, 0.04)',
  },
  '& .total-acuity-group-start': {
    backgroundColor: 'rgba(21, 101, 192, 0.04)',
    borderLeft: '2px solid rgba(21, 101, 192, 0.25)',
  },
  '& .rn-acuity-group': {
    backgroundColor: 'rgba(123, 31, 162, 0.04)',
  },
  '& .rn-acuity-group-start': {
    backgroundColor: 'rgba(123, 31, 162, 0.04)',
    borderLeft: '2px solid rgba(123, 31, 162, 0.25)',
  },
  // Grouped column header tints (match cell tints)
  '& .MuiDataGrid-columnHeader.total-acuity-header-start': {
    backgroundColor: 'rgba(21, 101, 192, 0.08)',
    borderLeft: '2px solid rgba(21, 101, 192, 0.25)',
    position: 'relative',
    overflow: 'visible',
    paddingTop: '28px',
  },
  '& .MuiDataGrid-columnHeader.total-acuity-header': {
    backgroundColor: 'rgba(21, 101, 192, 0.08)',
    paddingTop: '28px',
  },
  '& .MuiDataGrid-columnHeader.rn-acuity-header-start': {
    backgroundColor: 'rgba(123, 31, 162, 0.08)',
    borderLeft: '2px solid rgba(123, 31, 162, 0.25)',
    position: 'relative',
    overflow: 'visible',
    paddingTop: '28px',
  },
  '& .MuiDataGrid-columnHeader.rn-acuity-header': {
    backgroundColor: 'rgba(123, 31, 162, 0.08)',
    paddingTop: '28px',
  },
};

/* ───────────────── component ───────────────── */
export default function UnderstaffingScheme() {
  const navigate = useNavigate();
  const { stateCode } = useParams<{ stateCode: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Scheme state
  const [schemeId, setSchemeId] = useState<string | null>(null);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);

  // View toggle state (owners, affiliations, facilities)

  // State staffing requirements dialog
  const [staffingReqsOpen, setStaffingReqsOpen] = useState(false);

  // Metric tab state (which columns to show)
  const [metricTab, setMetricTab] = useState<'understaffing' | 'understaffing_mds' | 'citations' | 'ratings' | 'financials' | 'riskscore'>('understaffing');

  // Target list state (facilities)
  const [chains, setChains] = useState<ChainRow[]>([]);
  const [chainsLoading, setChainsLoading] = useState(false);
  const [stateFilter, setStateFilter] = useState<string>(stateCode || 'MI');  // From URL param or default MI
  const [acuityFilter, setAcuityFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'hybrid_mds_cost', sort: 'desc' }]);
  const [availableStates, setAvailableStates] = useState<string[]>([]);
  const [filteredAggregates, setFilteredAggregates] = useState<any>(null);

  // Benchmark comparison columns toggle
  const [showBenchmarks, setShowBenchmarks] = useState(false);

  // Ownership groups toggle state — init from URL param
  const [showOwnerGroups, setShowOwnerGroups] = useState(searchParams.get('view') !== 'facilities');
  const [ownerGroups, setOwnerGroups] = useState<any[]>([]);
  const [ownerGroupsLoading, setOwnerGroupsLoading] = useState(false);
  const [ownerGroupsRowCount, setOwnerGroupsRowCount] = useState(0);

  // Header expand/collapse
  const [schemeDetailsExpanded, setSchemeDetailsExpanded] = useState(false);

  // State context banner
  const [stateContext, setStateContext] = useState<Record<string, any> | null>(null);
  const [summaryModel, setSummaryModel] = useState<'anticipated' | 'hybrid'>('anticipated');

  // Expandable row state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleRowExpand = useCallback((rowId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  // Synthetic detail rows: when a row is expanded, insert a real detail row after it
  const processedOwnerGroupRows = useMemo(() => {
    if (!showOwnerGroups) return ownerGroups;
    const result: any[] = [];
    for (const row of ownerGroups) {
      result.push(row);
      if (expandedRows.has(row.group_id)) {
        result.push({
          _isDetailRow: true,
          _parentRow: row,
          group_id: `detail-${row.group_id}`,
        });
      }
    }
    return result;
  }, [ownerGroups, expandedRows, showOwnerGroups]);

  // Synthetic detail rows for facility view
  const processedFacilityRows = useMemo(() => {
    const result: any[] = [];
    for (const row of chains) {
      result.push(row);
      if (expandedRows.has(row.id)) {
        result.push({
          _isDetailRow: true,
          _parentRow: row,
          id: `detail-${row.id}`,
        });
      }
    }
    return result;
  }, [chains, expandedRows]);

  // Prosecution baselines for comparison (dynamically loaded from API)
  const [sweetwaterBaseline, setSweetwaterBaseline] = useState<ProsecutionBaseline | null>(null);
  const [njBaseline, setNjBaseline] = useState<ProsecutionBaseline | null>(null);

  // Citation benchmarks by facility size (from scheme.metadata)
  const [citationBenchmarks, setCitationBenchmarks] = useState<Record<string, Record<string, any>> | null>(null);

  // Derived HPRD values from baselines (with fallbacks)
  const sweetwaterHprd = sweetwaterBaseline?.nursing_hrs_per_patient_day ?? FALLBACK_sweetwaterHprd;
  const njHprd = njBaseline?.nursing_hrs_per_patient_day ?? FALLBACK_DEPTFORD_HPRD;

  // Derived acuity deficit values from baselines (value - 100, so negative = understaffed)
  const sweetwaterAcuityTotal = (sweetwaterBaseline?.ant_pct_acuity_staffed_total ?? 82.6) - 100;
  const sweetwaterAcuityRn = (sweetwaterBaseline?.ant_pct_acuity_staffed_rn ?? 18.6) - 100;
  const sweetwaterAcuityLpn = (sweetwaterBaseline?.ant_pct_acuity_staffed_lpn ?? 100) - 100;
  const sweetwaterAcuityAide = (sweetwaterBaseline?.ant_pct_acuity_staffed_cna ?? 100) - 100;
  const njAcuityTotal = (njBaseline?.ant_pct_acuity_staffed_total ?? 75.4) - 100;
  const njAcuityRn = (njBaseline?.ant_pct_acuity_staffed_rn ?? 43.3) - 100;
  const njAcuityLpn = (njBaseline?.ant_pct_acuity_staffed_lpn ?? 100) - 100;
  const njAcuityAide = (njBaseline?.ant_pct_acuity_staffed_cna ?? 100) - 100;

  // Columns where higher values are worse (default to desc to show worst first)
  const descDefaultColumns = useMemo(() => new Set([
    'total_understaffed_hprd_hrs',
    'total_mds_understaffing_cost',
    'total_mds_understaffing_cost_no_credit',
    'hybrid_mds_cost',
    'hybrid_mds_cost_no_credit',
    'total_harm_citations',
    'total_fine_amount',
    'risk_score',
    // Owner group columns
    'total_ant_cost',
    'total_ant_cost_no_credit',
    'hybrid_mds_cost_no_credit',
    // Individual owner columns
    'total_mds_understaffing_cost',
    'total_rp_paid',
    'total_rp_disallowed',
    'total_rp_plus_mds',
    'num_facilities',
  ]), []);

  // Columns where lower values are worse (default to asc to show worst first)
  const ascDefaultColumns = useMemo(() => new Set([
    'vs_sweetwater',
    'vs_sweetwater_rn',
    'vs_nj',
    'vs_nj_rn',
    'ant_pct_acuity_staffed_total',
    'ant_pct_acuity_staffed_rn',
    'ant_pct_acuity_staffed_lpn',
    'ant_pct_acuity_staffed_cna',
  ]), []);

  // Track the last field the user sorted by, so we can distinguish
  // "clicking a new column" from "cycling sort on the same column after unsorted"
  const lastSortFieldRef = React.useRef<string | null>(null);

  // Custom sort handler - default to appropriate direction for metric columns
  const handleSortModelChange = useCallback((newModel: GridSortModel) => {
    if (newModel.length > 0) {
      const field = newModel[0].field;
      const isNewField = field !== lastSortFieldRef.current;

      // First click on a new column - set appropriate default direction
      if (isNewField) {
        lastSortFieldRef.current = field;
        if (descDefaultColumns.has(field) && newModel[0].sort === 'asc') {
          setSortModel([{ field, sort: 'desc' }]);
          return;
        }
        if (ascDefaultColumns.has(field) && newModel[0].sort === 'desc') {
          setSortModel([{ field, sort: 'asc' }]);
          return;
        }
      }
    } else {
      // Clearing sort (unsorted) — keep lastSortFieldRef so next click
      // on the same column is treated as a cycle, not a new column
    }
    setSortModel(newModel);
  }, [descDefaultColumns, ascDefaultColumns]);

  // Fetch scheme ID and metadata on mount
  useEffect(() => {
    const fetchScheme = async () => {
      try {
        const res = await api.get('/schemes/');
        const schemes = res.data.results ?? res.data;
        const understaffingScheme = schemes.find((s: any) => s.code === 'snf-understaffing');
        if (understaffingScheme) {
          setSchemeId(understaffingScheme.id);
          // Store citation benchmarks by size from scheme metadata
          if (understaffingScheme.metadata?.citation_benchmarks_by_size) {
            setCitationBenchmarks(understaffingScheme.metadata.citation_benchmarks_by_size);
          }
        } else {
          console.error('SNF Understaffing scheme not found');
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch schemes', err);
        setLoading(false);
      }
    };
    fetchScheme();
  }, []);

  // Fetch summary when scheme ID is set
  useEffect(() => {
    if (!schemeId) return;
    const fetchSummary = async () => {
      try {
        const res = await api.get(`/schemes/${schemeId}/understaffing/summary/`);
        setSummary(res.data);
        setAvailableStates(res.data.states || []);
      } catch (err) {
        console.error('Failed to fetch summary', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [schemeId]);

  // Fetch target facilities
  const fetchTargets = async () => {
    if (!schemeId || showOwnerGroups) return;
    setChainsLoading(true);
    try {
      const params: any = {
        view: 'facilities',
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
      };
      // Add sorting - map frontend field names to backend fields
      if (sortModel.length > 0) {
        const fieldMap: Record<string, string> = {
          'vs_sweetwater': 'ant_pct_acuity_staffed_total',
          'vs_nj': 'ant_pct_acuity_staffed_total',
          'vs_sweetwater_rn': 'ant_pct_acuity_staffed_rn',
          'vs_nj_rn': 'ant_pct_acuity_staffed_rn',
        };
        params.sort = fieldMap[sortModel[0].field] || sortModel[0].field;
        params.order = sortModel[0].sort || 'desc';
      }
      if (stateFilter) params.state = stateFilter;
      if (acuityFilter) params.acuity_filter = acuityFilter;
      if (search) params.search = search;

      const res = await api.get(`/schemes/${schemeId}/understaffing/targets/`, { params });
      setChains(res.data.results || []);
      setRowCount(res.data.total_count || 0);
      if (res.data.filters?.states) {
        setAvailableStates(res.data.filters.states);
      }
      if (res.data.aggregates) {
        setFilteredAggregates(res.data.aggregates);
      }
      // Store prosecution baselines from response
      if (res.data.sweetwater_baseline) {
        setSweetwaterBaseline(res.data.sweetwater_baseline);
      }
      if (res.data.nj_baseline) {
        setNjBaseline(res.data.nj_baseline);
      }
    } catch (err) {
      console.error('Failed to fetch targets', err);
    } finally {
      setChainsLoading(false);
    }
  };

  useEffect(() => {
    fetchTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemeId, paginationModel.page, paginationModel.pageSize, sortModel, stateFilter, acuityFilter, search, showOwnerGroups]);

  // Fetch ownership groups when toggle is active
  const fetchOwnerGroups = async () => {
    if (!schemeId || !stateFilter) return;
    setOwnerGroupsLoading(true);
    try {
      const params: any = {
        state: stateFilter,
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
      };
      if (sortModel.length > 0) {
        const ogFieldMap: Record<string, string> = {
          'og_vs_sweetwater': 'ant_pct_acuity_staffed_total',
          'og_vs_nj': 'ant_pct_acuity_staffed_total',
          'og_rn_gap': 'ant_pct_acuity_staffed_rn',
          'og_vs_sweetwater_rn': 'ant_pct_acuity_staffed_rn',
          'og_vs_nj_rn': 'ant_pct_acuity_staffed_rn',
        };
        params.sort = ogFieldMap[sortModel[0].field] || sortModel[0].field;
        params.order = sortModel[0].sort || 'desc';
      }
      if (search) params.search = search;
      if (acuityFilter) params.acuity_filter = acuityFilter;

      const res = await api.get(`/schemes/${schemeId}/understaffing/owner-groups-state/`, { params });
      setOwnerGroups(res.data.results || []);
      setOwnerGroupsRowCount(res.data.total_count || 0);
      // Update baselines from owner groups response if not already loaded
      if (res.data.sweetwater_baseline && !sweetwaterBaseline) {
        setSweetwaterBaseline(res.data.sweetwater_baseline);
      }
      if (res.data.nj_baseline && !njBaseline) {
        setNjBaseline(res.data.nj_baseline);
      }
    } catch (err) {
      console.error('Failed to fetch owner groups', err);
    } finally {
      setOwnerGroupsLoading(false);
    }
  };

  useEffect(() => {
    if (showOwnerGroups) {
      fetchOwnerGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOwnerGroups, schemeId, paginationModel.page, paginationModel.pageSize, sortModel, stateFilter, search, acuityFilter]);

  // Fetch state context banner when stateFilter changes
  useEffect(() => {
    if (!schemeId || !stateFilter) {
      setStateContext(null);
      return;
    }
    const fetchStateContext = async () => {
      try {
        const res = await api.get(`/schemes/${schemeId}/understaffing/state-context/`, {
          params: { state: stateFilter },
        });
        setStateContext(res.data);
      } catch (err) {
        console.error('Failed to fetch state context', err);
      }
    };
    fetchStateContext();
  }, [schemeId, stateFilter]);

  // Handle metric tab change - update default sort
  const handleMetricTabChange = (_event: React.SyntheticEvent, newTab: 'understaffing' | 'understaffing_mds' | 'citations' | 'ratings' | 'financials' | 'riskscore') => {
    setMetricTab(newTab);
    // Set default sort based on new tab
    const defaultSorts: Record<string, { field: string; sort: 'asc' | 'desc' }> = {
      understaffing: { field: 'total_understaffed_hprd_hrs', sort: 'desc' },
      understaffing_mds: { field: 'total_mds_understaffing_cost', sort: 'desc' },
      citations: { field: 'total_harm_citations', sort: 'desc' },
      ratings: { field: 'avg_overall_rating', sort: 'desc' },
      financials: { field: 'total_rp_total', sort: 'desc' },
      riskscore: { field: 'risk_score', sort: 'desc' },
    };
    setSortModel([defaultSorts[newTab]]);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // Sort options for prosecutor view - using backend field names
  const getSortOptions = () => {
    return [
      { value: 'total_ant_understaffed_hrs', label: 'MDS Understaffed Hrs' },
      { value: 'ant_understaffed_rn_hrs_prd', label: 'MDS Hrs Short / Day' },
      { value: 'avg_cms_total_staffing_hrd', label: 'Nursing Hrs/Patient/Day' },
      { value: 'total_understaffed_hprd_hrs', label: 'HPRD Understaffed Hrs' },
      { value: 'risk_score', label: 'Risk Score' },
    ];
    // Legacy switch kept for reference but not used
    switch (metricTab) {
      case 'understaffing':
        return [
          { value: 'total_understaffed_hprd_hrs', label: 'Understaffed Hours' },
          { value: 'understaffed_hrs_per_res_day', label: 'Hrs Short / Res Day' },
          { value: 'total_understaffed_hprd_cost', label: 'Understaffed Cost' },
          { value: 'pct_days_below_cms_hprd', label: '% Days Below Min' },
        ];
      case 'understaffing_mds':
        return [
          { value: 'total_ant_understaffed_hrs', label: 'MDS Understaffed Hrs' },
          { value: 'total_mds_understaffing_cost', label: 'MDS Understaffed Cost' },
          { value: 'ant_understaffed_rn_hrs_prd', label: 'RN Hrs Short / Res Day' },
          { value: 'ant_understaffed_lpn_hrs_prd', label: 'LPN Hrs Short / Res Day' },
          { value: 'ant_understaffed_cna_hrs_prd', label: 'Aide Hrs Short / Res Day' },
        ];
      case 'citations':
        return [
          { value: 'total_harm_citations', label: 'Harm Citations' },
          { value: 'harm_per_100', label: 'Harm / 100 Residents' },
          { value: 'total_citations', label: 'Total Citations' },
          { value: 'total_citations_per_100', label: 'Citations / 100 Res' },
          { value: 'total_fine_amount', label: 'Fines' },
        ];
      case 'ratings':
        return [
          { value: 'avg_overall_rating', label: 'Overall Rating' },
          { value: 'avg_health_inspection_rating', label: 'Health Inspection' },
          { value: 'avg_staffing_rating', label: 'Staffing Rating' },
          { value: 'avg_qm_rating', label: 'QM Rating' },
          { value: 'avg_nursing_turnover', label: 'Nursing Turnover' },
          { value: 'num_facilities', label: '# Facilities' },
        ];
      case 'financials':
        return [
          { value: 'total_rp_total', label: 'RP Total' },
          { value: 'rp_total_per_resident_day', label: 'RP / Res Day' },
          { value: 'rp_total_pct_gross_rev', label: 'RP % Revenue' },
          { value: 'rp_mgmt_pct_gross_rev', label: 'RP Mgmt % Rev' },
          { value: 'total_rp_real_estate', label: 'RP Real Estate' },
          { value: 'rp_real_estate_pct_gross_rev', label: 'RP RE % Rev' },
          { value: 'admin_mgmt_pct_gross_rev', label: 'Admin % Rev' },
        ];
      case 'riskscore':
        return [
          { value: 'risk_score', label: 'Overall Risk Score' },
          { value: 'understaffing_score', label: 'Understaffing Score' },
          { value: 'harm_citations_score', label: 'Harm Citations Score' },
          { value: 'penalties_score', label: 'Penalties Score' },
          { value: 'ratings_score', label: 'Ratings Score' },
          { value: 'staff_quality_score', label: 'Staff Quality Score' },
          { value: 'related_party_score', label: 'Related Party Score' },
        ];
      default:
        return [{ value: 'total_understaffed_hprd_hrs', label: 'Understaffed Hours' }];
    }
  };

  // Handle chain row click — navigate to facility detail page
  const handleChainClick = (chain: ChainRow) => {
    const ccn = chain.metadata?.ccn || chain.ccn_list?.[0];
    if (ccn) {
      navigate(`/schemes/snf-understaffing/facility/${ccn}`, {
        state: { fromState: stateFilter },
      });
    }
  };

  // Name columns
  const facilityNameColumn: GridColDef<ChainRow> = {
    field: 'name',
    headerName: 'Facility',
    flex: 2,
    minWidth: 250,
    renderCell: ({ row }) => (
      <Box sx={{ whiteSpace: 'normal', lineHeight: 1.3, py: 0.5 }}>
        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
          {row.dba_name || stripStateFromName(row.name)}
        </Typography>
      </Box>
    ),
  };

  const facilityAffiliationColumn: GridColDef<ChainRow> = {
    field: 'affiliation',
    headerName: 'Affiliation',
    flex: 1.5,
    minWidth: 150,
    renderCell: ({ row }) => (
      <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {row.metadata?.affiliated_entity_name || ''}
      </Typography>
    ),
  };

  const stateColumn: GridColDef<ChainRow> = {
    field: 'state',
    headerName: 'State',
    width: 80,
  };


  // ─────────────── METRIC COLUMN GROUPS ───────────────
  // Helper to render column header with tooltip
  const renderHeaderWithTooltip = (headerName: string, _tooltip: string, subtitle?: string) => () => (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {headerName}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', lineHeight: 1 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  // Understaffing columns - dynamic based on state requirements
  const getUnderstaffingColumns = (): GridColDef<ChainRow>[] => {
    const stateReqs = stateFilter ? STATE_STAFFING_REQS[stateFilter] : null;

    const baseColumns: GridColDef<ChainRow>[] = [
      {
        field: 'total_understaffed_hprd_hrs',
        headerName: 'HPRD Understaffed Hrs',
        flex: 1,
        minWidth: 150,
        renderHeader: renderHeaderWithTooltip('HPRD Understaffed Hrs', 'Total hours below state minimum Hours Per Resident Day (HPRD) staffing requirements. Sums daily shortfalls on understaffed days.'),
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight={500}>
            {formatHours(row.total_understaffed_hprd_hrs)}
          </Typography>
        ),
      },
      {
        field: 'understaffed_hrs_per_res_day',
        headerName: 'HPRD Hrs Short / Res Day',
        flex: 1,
        minWidth: 170,
        renderHeader: renderHeaderWithTooltip('HPRD Hrs Short / Res Day', 'Average hours each resident was shorted per day compared to state minimum staffing requirements.'),
        renderCell: ({ row }) => {
          const value = row.metadata?.understaffed_state_min_hprd_hrs_per_res_day;
          return (
            <Box display="flex" alignItems="center">
              <Typography variant="body2" fontWeight={500}>
                {value != null ? `${Number(value).toFixed(2)} hrs` : '-'}
              </Typography>
              <SweetwaterComparison
                value={value}
                baseline={sweetwaterBaseline?.understaffed_state_min_hprd_hrs_per_res_day}
                higherIsWorse={true}
                decimals={2}
              />
            </Box>
          );
        },
      },
      {
        field: 'total_understaffed_hprd_cost',
        headerName: 'HPRD Understaffed Cost',
        flex: 1,
        minWidth: 160,
        renderHeader: renderHeaderWithTooltip('HPRD Understaffed Cost', 'Estimated labor cost savings from understaffing. Calculated by multiplying understaffed hours by average CNA wages from cost reports.'),
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight={500}>
            {formatMoney(row.total_understaffed_hprd_cost)}
          </Typography>
        ),
      },
      {
        field: 'pct_days_below_cms_hprd',
        headerName: '% Days Below Min HPRD',
        flex: 1,
        minWidth: 170,
        renderHeader: renderHeaderWithTooltip('% Days Below Min HPRD', 'Percentage of days facility operated below state minimum HPRD requirements. Higher = more frequent understaffing.'),
        renderCell: ({ row }) => (
          <Box display="flex" alignItems="center">
            <Chip
              size="small"
              label={formatPercent(row.pct_days_below_cms_hprd)}
              color={row.pct_days_below_cms_hprd && row.pct_days_below_cms_hprd > 50 ? 'error' : 'default'}
              variant="outlined"
            />
            <SweetwaterComparison
              value={row.pct_days_below_cms_hprd}
              baseline={sweetwaterBaseline?.pct_days_below_cms_hprd}
              higherIsWorse={true}
              format="percent"
            />
          </Box>
        ),
      },
    ];

    // Add CNA columns if state has CNA requirement
    if (stateReqs?.cna) {
      baseColumns.push({
        field: 'total_understaffed_state_min_cna_hrs',
        headerName: `CNA Understaffed Hrs`,
        flex: 1,
        minWidth: 150,
        renderHeader: renderHeaderWithTooltip('CNA Understaffed Hrs', `Hours below ${stateReqs.cna} CNA hours/patient day state requirement for Certified Nursing Assistants.`),
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight={500}>
            {formatHours(row.metadata?.total_understaffed_state_min_cna_hrs)}
          </Typography>
        ),
      });
      baseColumns.push({
        field: 'understaffed_state_min_cna_hrs_per_res_day',
        headerName: `CNA Hrs Short / Res Day`,
        flex: 1,
        minWidth: 160,
        renderHeader: renderHeaderWithTooltip('CNA Hrs Short / Res Day', `Average CNA hours each resident was shorted per day vs ${stateReqs.cna} hrs/day state requirement.`),
        renderCell: ({ row }) => {
          const value = row.metadata?.understaffed_state_min_cna_hrs_per_res_day;
          return (
            <Typography variant="body2" fontWeight={500}>
              {value != null ? `${Number(value).toFixed(2)} hrs` : '-'}
            </Typography>
          );
        },
      });
    }

    // Add RN columns if state has RN requirement
    if (stateReqs?.rnHoursPerDay) {
      baseColumns.push({
        field: 'total_understaffed_state_min_rn_hrs',
        headerName: `RN Understaffed Hrs`,
        flex: 1,
        minWidth: 150,
        renderHeader: renderHeaderWithTooltip('RN Understaffed Hrs', `Hours below ${stateReqs.rnHoursPerDay} RN hours/day state requirement for Registered Nurses.`),
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight={500}>
            {formatHours(row.metadata?.total_understaffed_state_min_rn_hrs)}
          </Typography>
        ),
      });
      baseColumns.push({
        field: 'understaffed_state_min_rn_hrs_per_res_day',
        headerName: `RN Hrs Short / Res Day`,
        flex: 1,
        minWidth: 160,
        renderHeader: renderHeaderWithTooltip('RN Hrs Short / Res Day', `Average RN hours short per resident day vs ${stateReqs.rnHoursPerDay} hrs/day state requirement.`),
        renderCell: ({ row }) => {
          const value = row.metadata?.understaffed_state_min_rn_hrs_per_res_day;
          return (
            <Typography variant="body2" fontWeight={500}>
              {value != null ? `${Number(value).toFixed(2)} hrs` : '-'}
            </Typography>
          );
        },
      });
    }

    return baseColumns;
  };

  // MDS Understaffing columns (acuity-based staffing requirements)
  const getMdsUnderstaffingColumns = (): GridColDef<ChainRow>[] => {
    return [
      {
        field: 'total_ant_understaffed_hrs',
        headerName: 'MDS Understaffed Hrs',
        flex: 1,
        minWidth: 160,
        valueGetter: (value, row) => row.metadata?.total_ant_understaffed_hrs,
        renderHeader: renderHeaderWithTooltip('MDS Understaffed Hrs', 'Total hours below expected staffing based on MDS acuity scores (RN + LPN + Aide combined). MDS calculates expected staffing based on resident case-mix and care needs.'),
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight={500}>
            {formatHours(row.metadata?.total_ant_understaffed_hrs)}
          </Typography>
        ),
      },
      {
        field: 'total_mds_understaffing_cost',
        headerName: 'Cost w/ Staffing Credit (Anticipated)',
        flex: 1,
        minWidth: 170,
        valueGetter: (value, row) => row.metadata?.total_mds_understaffing_cost,
        renderHeader: renderHeaderWithTooltip('Cost w/ Staffing Credit', 'Anticipated model: Total labor cost of MDS acuity understaffing WITH credit for overstaffed categories (overstaffing in one role offsets understaffing in another).', 'Anticipated'),
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight={500}>
            {formatMoney(row.metadata?.total_mds_understaffing_cost)}
          </Typography>
        ),
      },
      {
        field: 'total_mds_understaffing_cost_no_credit',
        headerName: 'Cost No Staffing Credit (Anticipated)',
        flex: 1,
        minWidth: 170,
        valueGetter: (value, row) => row.metadata?.total_mds_understaffing_cost_no_credit,
        renderHeader: renderHeaderWithTooltip('Cost No Staffing Credit', 'Anticipated model: Understaffing cost with NO credit for overstaffed categories. Only counts days/hours where actual < expected.', 'Anticipated'),
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight={500}>
            {formatMoney(row.metadata?.total_mds_understaffing_cost_no_credit)}
          </Typography>
        ),
      },
      {
        field: 'hybrid_mds_cost',
        headerName: 'Cost w/ Staffing Credit (Hybrid)',
        flex: 1,
        minWidth: 170,
        valueGetter: (value, row) => row.metadata?.hybrid_mds_cost,
        renderHeader: renderHeaderWithTooltip('Cost w/ Staffing Credit', 'Hybrid model: Uses CMS recommended staffing rates (higher aide requirements) WITH credit for overstaffed categories.', 'Hybrid'),
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight={500}>
            {formatMoney(row.metadata?.hybrid_mds_cost)}
          </Typography>
        ),
      },
      {
        field: 'hybrid_mds_cost_no_credit',
        headerName: 'Cost No Staffing Credit (Hybrid)',
        flex: 1,
        minWidth: 170,
        valueGetter: (value, row) => row.metadata?.hybrid_mds_cost_no_credit,
        renderHeader: renderHeaderWithTooltip('Cost No Staffing Credit', 'Hybrid model: Understaffing cost with NO credit for overstaffed categories. Only counts days/hours where actual < expected.', 'Hybrid'),
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight={500}>
            {formatMoney(row.metadata?.hybrid_mds_cost_no_credit)}
          </Typography>
        ),
      },
      {
        field: 'ant_understaffed_rn_hrs_prd',
        headerName: 'RN Hrs Short / Res Day',
        flex: 1,
        minWidth: 170,
        valueGetter: (value, row) => row.metadata?.ant_understaffed_rn_hrs_prd,
        renderHeader: renderHeaderWithTooltip('RN Hrs Short / Res Day', 'Average RN hours each resident was shorted per day vs MDS acuity-based expected staffing.'),
        renderCell: ({ row }) => {
          const value = row.metadata?.ant_understaffed_rn_hrs_prd;
          return (
            <Typography variant="body2" fontWeight={500}>
              {value != null ? `${Number(value).toFixed(2)} hrs` : '-'}
            </Typography>
          );
        },
      },
      {
        field: 'ant_understaffed_lpn_hrs_prd',
        headerName: 'LPN Hrs Short / Res Day',
        flex: 1,
        minWidth: 170,
        valueGetter: (value, row) => row.metadata?.ant_understaffed_lpn_hrs_prd,
        renderHeader: renderHeaderWithTooltip('LPN Hrs Short / Res Day', 'Average LPN hours each resident was shorted per day vs MDS acuity-based expected staffing.'),
        renderCell: ({ row }) => {
          const value = row.metadata?.ant_understaffed_lpn_hrs_prd;
          return (
            <Typography variant="body2" fontWeight={500}>
              {value != null ? `${Number(value).toFixed(2)} hrs` : '-'}
            </Typography>
          );
        },
      },
      {
        field: 'ant_understaffed_cna_hrs_prd',
        headerName: 'Aide Hrs Short / Res Day',
        flex: 1,
        minWidth: 180,
        valueGetter: (value, row) => row.metadata?.ant_understaffed_cna_hrs_prd,
        renderHeader: renderHeaderWithTooltip('Aide Hrs Short / Res Day', 'Average Aide hours each resident was shorted per day vs MDS acuity-based expected staffing.'),
        renderCell: ({ row }) => {
          const value = row.metadata?.ant_understaffed_cna_hrs_prd;
          return (
            <Typography variant="body2" fontWeight={500}>
              {value != null ? `${Number(value).toFixed(2)} hrs` : '-'}
            </Typography>
          );
        },
      },
    ];
  };

  // Citations columns
  const citationsColumns: GridColDef<ChainRow>[] = [
    {
      field: 'total_citations',
      headerName: 'Total Citations',
      flex: 1,
      minWidth: 120,
      renderHeader: renderHeaderWithTooltip('Total Citations', 'Total deficiency citations issued by CMS during health inspections. Each citation represents a federal nursing home requirement violation.'),
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatNumber(row.total_citations ?? row.metadata?.total_citations)}</Typography>
      ),
    },
    {
      field: 'total_citations_per_100',
      headerName: 'Citations / 100 Res',
      flex: 1,
      minWidth: 140,
      renderHeader: renderHeaderWithTooltip('Citations / 100 Res', 'Citations normalized by facility size (per 100 residents). Allows fair comparison between facilities of different sizes.'),
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center">
          <Typography variant="body2">
            {formatNumber(row.total_citations_per_100 ?? row.metadata?.total_citations_per_100, 1)}
          </Typography>
          <SweetwaterComparison
            value={row.total_citations_per_100 ?? row.metadata?.total_citations_per_100}
            baseline={sweetwaterBaseline?.total_citations_per_100}
            higherIsWorse={true}
          />
        </Box>
      ),
    },
    {
      field: 'total_harm_citations',
      headerName: 'Harm Citations',
      flex: 1,
      minWidth: 120,
      renderHeader: renderHeaderWithTooltip('Harm Citations', 'Citations with severity level G or higher, indicating actual harm or immediate jeopardy to residents. These are the most serious violations.'),
      renderCell: ({ row }) => (
        <Typography variant="body2" fontWeight={500}>
          {formatNumber(row.total_harm_citations)}
        </Typography>
      ),
    },
    {
      field: 'harm_per_100',
      headerName: 'Harm / 100 Res',
      flex: 1,
      minWidth: 130,
      renderHeader: renderHeaderWithTooltip('Harm / 100 Res', 'Harm citations normalized by facility size (per 100 residents). Allows comparison of serious violations across facilities of different sizes.'),
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center">
          <Typography variant="body2">
            {row.metadata?.harm_per_100_residents != null
              ? Number(row.metadata.harm_per_100_residents).toFixed(1)
              : '-'}
          </Typography>
          <SweetwaterComparison
            value={row.metadata?.harm_per_100_residents}
            baseline={sweetwaterBaseline?.harm_per_100_residents}
            higherIsWorse={true}
          />
        </Box>
      ),
    },
    {
      field: 'total_fine_amount',
      headerName: 'Fines',
      flex: 1,
      minWidth: 100,
      renderHeader: renderHeaderWithTooltip('Fines', 'Total monetary penalties (Civil Money Penalties) imposed by CMS for violations found during inspections.'),
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatMoney(row.total_fine_amount)}</Typography>
      ),
    },
  ];

  // Ratings columns
  const ratingsColumns: GridColDef<ChainRow>[] = [
    {
      field: 'avg_overall_rating',
      headerName: 'Overall Rating',
      flex: 1,
      minWidth: 120,
      renderHeader: renderHeaderWithTooltip('Overall Rating', 'CMS Five-Star Overall Rating (1-5 stars). Combines health inspection, staffing, and quality measure ratings. Higher = better.'),
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center">
          <Chip
            size="small"
            label={row.avg_overall_rating?.toFixed(1) || '-'}
            color={getRatingColor(row.avg_overall_rating) as any}
            variant="filled"
          />
          <SweetwaterComparison
            value={row.avg_overall_rating}
            baseline={sweetwaterBaseline?.avg_overall_rating}
            higherIsWorse={false}
          />
        </Box>
      ),
    },
    {
      field: 'avg_health_inspection_rating',
      headerName: 'Health Inspection',
      flex: 1,
      minWidth: 130,
      renderHeader: renderHeaderWithTooltip('Health Inspection', 'CMS Health Inspection Rating (1-5 stars). Based on findings from the most recent 3 years of health inspections. Lower = more deficiencies.'),
      renderCell: ({ row }) => (
        <Chip
          size="small"
          label={row.metadata?.avg_health_inspection_rating?.toFixed(1) || '-'}
          color={getRatingColor(row.metadata?.avg_health_inspection_rating) as any}
          variant="filled"
        />
      ),
    },
    {
      field: 'avg_staffing_rating',
      headerName: 'Staffing Rating',
      flex: 1,
      minWidth: 120,
      renderHeader: renderHeaderWithTooltip('Staffing Rating', 'CMS Staffing Rating (1-5 stars). Based on total nurse staffing hours per resident day, adjusted for case-mix. From PBJ data.'),
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center">
          <Chip
            size="small"
            label={row.avg_staffing_rating?.toFixed(1) || '-'}
            color={getRatingColor(row.avg_staffing_rating) as any}
            variant="filled"
          />
          <SweetwaterComparison
            value={row.avg_staffing_rating}
            baseline={sweetwaterBaseline?.avg_staffing_rating}
            higherIsWorse={false}
          />
        </Box>
      ),
    },
    {
      field: 'avg_qm_rating',
      headerName: 'QM Rating',
      flex: 1,
      minWidth: 110,
      renderHeader: renderHeaderWithTooltip('QM Rating', 'CMS Quality Measures Rating (1-5 stars). Based on clinical outcomes like falls, pressure ulcers, and hospitalizations. From MDS assessments.'),
      renderCell: ({ row }) => (
        <Chip
          size="small"
          label={row.metadata?.avg_qm_rating?.toFixed(1) || '-'}
          color={getRatingColor(row.metadata?.avg_qm_rating) as any}
          variant="filled"
        />
      ),
    },
    {
      field: 'avg_nursing_turnover',
      headerName: 'Nursing Turnover',
      flex: 1,
      minWidth: 130,
      renderHeader: renderHeaderWithTooltip('Nursing Turnover', 'Percentage of nursing staff (RNs, LPNs, CNAs) who left the facility in the past year. High turnover is associated with lower quality care.'),
      renderCell: ({ row }) => (
        <Typography variant="body2">
          {formatPercent(row.metadata?.avg_nursing_turnover)}
        </Typography>
      ),
    },
    {
      field: 'num_facilities',
      headerName: '# Facilities',
      flex: 0.8,
      minWidth: 100,
      renderHeader: renderHeaderWithTooltip('# Facilities', 'Number of nursing home facilities owned or affiliated with this entity.'),
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatNumber(row.num_facilities)}</Typography>
      ),
    },
  ];

  // Financials columns (Related Party)
  const financialsColumns: GridColDef<ChainRow>[] = [
    {
      field: 'total_rp_total',
      headerName: 'RP Total',
      flex: 1,
      minWidth: 120,
      renderHeader: renderHeaderWithTooltip('RP Total', 'Total payments to related parties (entities with common ownership). Includes management fees, rent, supplies, and other services from cost reports.'),
      renderCell: ({ row }) => (
        <Typography variant="body2" fontWeight={500}>
          {formatMoney(row.metadata?.total_rp_total)}
        </Typography>
      ),
    },
    {
      field: 'rp_total_per_resident_day',
      headerName: 'RP / Res Day',
      flex: 1,
      minWidth: 120,
      renderHeader: renderHeaderWithTooltip('RP / Res Day', 'Related party payments divided by total resident days. Shows daily cost per resident attributable to related party transactions.'),
      renderCell: ({ row }) => (
        <Typography variant="body2">
          {row.metadata?.rp_total_per_resident_day != null
            ? `$${Number(row.metadata.rp_total_per_resident_day).toFixed(2)}`
            : '-'}
        </Typography>
      ),
    },
    {
      field: 'rp_total_pct_gross_rev',
      headerName: 'RP % Revenue',
      flex: 1,
      minWidth: 120,
      renderHeader: renderHeaderWithTooltip('RP % Revenue', 'Related party payments as a percentage of total gross revenue. Higher = more revenue flowing to related entities.'),
      renderCell: ({ row }) => (
        <Typography variant="body2">
          {formatPercent(row.metadata?.rp_total_pct_gross_rev)}
        </Typography>
      ),
    },
    {
      field: 'rp_mgmt_pct_gross_rev',
      headerName: 'RP Mgmt % Rev',
      flex: 1,
      minWidth: 120,
      renderHeader: renderHeaderWithTooltip('RP Mgmt % Rev', 'Management fees paid to related parties as a percentage of gross revenue. Management fees are often used to extract profits from facilities.'),
      renderCell: ({ row }) => (
        <Typography variant="body2">
          {formatPercent(row.metadata?.rp_mgmt_pct_gross_rev)}
        </Typography>
      ),
    },
    {
      field: 'total_rp_real_estate',
      headerName: 'RP Real Estate',
      flex: 1,
      minWidth: 120,
      renderHeader: renderHeaderWithTooltip('RP Real Estate', 'Rent and real estate payments to related parties. Includes building rent, land rent, and equipment rent paid to entities with common ownership.'),
      renderCell: ({ row }) => (
        <Typography variant="body2">
          {formatMoney(row.metadata?.total_rp_real_estate)}
        </Typography>
      ),
    },
    {
      field: 'rp_real_estate_pct_gross_rev',
      headerName: 'RP RE % Rev',
      flex: 1,
      minWidth: 110,
      renderHeader: renderHeaderWithTooltip('RP RE % Rev', 'Related party real estate payments as a percentage of gross revenue. High values may indicate property-based profit extraction.'),
      renderCell: ({ row }) => (
        <Typography variant="body2">
          {formatPercent(row.metadata?.rp_real_estate_pct_gross_rev)}
        </Typography>
      ),
    },
    {
      field: 'admin_mgmt_pct_gross_rev',
      headerName: 'Admin % Rev',
      flex: 1,
      minWidth: 120,
      renderHeader: renderHeaderWithTooltip('Admin % Rev', 'Total administrative and general management costs as a percentage of gross revenue. Includes all management overhead (not just related party).'),
      renderCell: ({ row }) => (
        <Typography variant="body2">
          {formatPercent(row.metadata?.admin_mgmt_pct_gross_rev)}
        </Typography>
      ),
    },
  ];

  // Risk Score columns (shown in the "Risk Score" metric tab)
  const riskScoreColumns: GridColDef<ChainRow>[] = [
    {
      field: 'risk_score',
      headerName: 'Risk Score',
      flex: 1,
      minWidth: 120,
      renderHeader: renderHeaderWithTooltip('Risk Score', 'Composite risk score (0-100) across 10 factors: acuity gap, days below acuity, understaffing cost, staffing/abuse citations, RP payments, RP flags, and star rating. Higher = higher risk.'),
      valueGetter: (_value, row) => row.metadata?.risk_score ?? null,
      renderCell: ({ row }) => {
        const score = row.metadata?.risk_score;
        if (score == null) return <Typography variant="body2">-</Typography>;
        const level = row.metadata?.risk_level || '';
        const color = level === 'critical' ? 'error.main' : level === 'high' ? 'warning.dark' : level === 'elevated' ? 'warning.light' : level === 'moderate' ? 'info.main' : 'success.main';
        return <Typography variant="body2" fontWeight={700} sx={{ color }}>{score}</Typography>;
      },
    },
    {
      field: 'factor1_score',
      headerName: 'Acuity Gap',
      flex: 1,
      minWidth: 100,
      renderHeader: renderHeaderWithTooltip('Acuity Gap', 'State percentile for total acuity gap (20% weight). Higher = more understaffed vs patient acuity needs.'),
      valueGetter: (_value, row) => row.metadata?.factor1_score ?? null,
      renderCell: ({ row }) => {
        const v = row.metadata?.factor1_score;
        return <Typography variant="body2">{v != null ? v : '-'}</Typography>;
      },
    },
    {
      field: 'factor2_score',
      headerName: '% Days',
      flex: 1,
      minWidth: 90,
      renderHeader: renderHeaderWithTooltip('% Days Below', 'State percentile for % days failing to staff to acuity (15% weight).'),
      valueGetter: (_value, row) => row.metadata?.factor2_score ?? null,
      renderCell: ({ row }) => {
        const v = row.metadata?.factor2_score;
        return <Typography variant="body2">{v != null ? v : '-'}</Typography>;
      },
    },
    {
      field: 'factor3_score',
      headerName: 'Understaffing $',
      flex: 1,
      minWidth: 110,
      renderHeader: renderHeaderWithTooltip('Understaffing $', 'State percentile for projected understaffing cost without credit (20% weight).'),
      valueGetter: (_value, row) => row.metadata?.factor3_score ?? null,
      renderCell: ({ row }) => {
        const v = row.metadata?.factor3_score;
        return <Typography variant="body2">{v != null ? v : '-'}</Typography>;
      },
    },
    {
      field: 'factor4_score',
      headerName: 'Staff Cites',
      flex: 1,
      minWidth: 100,
      renderHeader: renderHeaderWithTooltip('Staffing Citations', 'State percentile for understaffing + staffing-related citations (5% weight).'),
      valueGetter: (_value, row) => row.metadata?.factor4_score ?? null,
      renderCell: ({ row }) => {
        const v = row.metadata?.factor4_score;
        return <Typography variant="body2">{v != null ? v : '-'}</Typography>;
      },
    },
    {
      field: 'factor5_score',
      headerName: 'Abuse',
      flex: 1,
      minWidth: 90,
      renderHeader: renderHeaderWithTooltip('Abuse Citations', 'State percentile for abuse/neglect citations (5% weight).'),
      valueGetter: (_value, row) => row.metadata?.factor5_score ?? null,
      renderCell: ({ row }) => {
        const v = row.metadata?.factor5_score;
        return <Typography variant="body2">{v != null ? v : '-'}</Typography>;
      },
    },
    {
      field: 'state_percentile',
      headerName: 'State Rank',
      flex: 1,
      minWidth: 110,
      renderHeader: renderHeaderWithTooltip('State Rank', 'Percentile rank of composite risk score within state. 100 = worst.'),
      valueGetter: (_value, row) => row.metadata?.state_percentile ?? null,
      renderCell: ({ row }) => {
        const v = row.metadata?.state_percentile;
        return <Typography variant="body2">{v != null ? `${v}%` : '-'}</Typography>;
      },
    },
  ];


  // Get metric columns based on active tab
  const getMetricColumns = (): GridColDef<ChainRow>[] => {
    switch (metricTab) {
      case 'understaffing':
        return getUnderstaffingColumns();
      case 'understaffing_mds':
        return getMdsUnderstaffingColumns();
      case 'citations':
        return citationsColumns;
      case 'ratings':
        return ratingsColumns;
      case 'financials':
        return financialsColumns;
      case 'riskscore':
        return riskScoreColumns;
      default:
        return getUnderstaffingColumns();
    }
  };

  // ─────────────── INDIVIDUAL OWNERS COLUMNS ───────────────
  const individualColumns: GridColDef[] = useMemo(() => [
    {
      field: 'owner_name',
      headerName: 'Owner Name',
      flex: 1.5,
      minWidth: 180,
      sortable: true,
      renderCell: ({ row }: any) => {
        const names = (row.owner_name || '').split('; ');
        return (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 1.3, py: 0.5 }}>
            {names.map((name: string, i: number) => (
              <Typography key={i} variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                {name}
              </Typography>
            ))}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
              {row.type_owner === 'I' && (
                <Chip label="Individual" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
              )}
              {row.type_owner === 'O' && (
                <Chip label="Organization" size="small" color="default" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
              )}
              {row.type_owner === 'M' && (
                <Chip label="Mixed" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
              )}
              {row.owner_count > 1 && (
                <Chip label={`${row.owner_count} owners`} size="small" color="secondary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'num_facilities',
      headerName: 'Facilities',
      width: 100,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
    },
    {
      field: 'avg_risk_score',
      headerName: 'Avg Risk',
      width: 110,
      sortingOrder: ['desc', 'asc', null],
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderCell: ({ row }: any) => {
        const score = row.avg_risk_score;
        if (score == null) return <Typography variant="body2">-</Typography>;
        const level = score >= 81 ? 'Critical' : score >= 61 ? 'High' : score >= 41 ? 'Elevated' : score >= 21 ? 'Moderate' : 'Low';
        const bgColor = score >= 81 ? '#d32f2f' : score >= 61 ? '#e65100' : score >= 41 ? '#c17900' : score >= 21 ? '#558b2f' : '#2e7d32';
        return (
          <Tooltip title={`Avg facility risk: ${Math.round(score)} (${level})`} arrow>
            <Box sx={{
              backgroundColor: bgColor,
              color: '#fff',
              borderRadius: '12px',
              px: 1.2,
              py: 0.2,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 36,
              cursor: 'pointer',
            }}>
              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                {Math.round(score)}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'years',
      headerName: 'Years',
      width: 140,
      sortable: false,
      renderCell: ({ row }: any) => {
        const years: number[] = row.years || [];
        if (years.length === 0) return '-';
        const sorted = [...years].sort();
        return `${sorted[0]}\u2013${sorted[sorted.length - 1]}`;
      },
    },
    {
      field: 'total_mds_understaffing_cost',
      headerName: 'Projected Understaffing Cost w/ Staffing Credit',
      flex: 1,
      minWidth: 140,
      sortable: true,
      align: 'right' as const,
      headerAlign: 'right' as const,
      renderCell: ({ row }: any) => formatMoney(row.total_mds_understaffing_cost),
    },
    {
      field: 'total_mds_understaffing_cost_no_credit',
      headerName: 'Projected Understaffing Cost No Staffing Credit',
      flex: 1,
      minWidth: 140,
      sortable: true,
      align: 'right' as const,
      headerAlign: 'right' as const,
      renderCell: ({ row }: any) => formatMoney(row.total_mds_understaffing_cost_no_credit),
    },
    {
      field: 'total_rp_paid',
      headerName: 'RP Paid',
      flex: 1,
      minWidth: 120,
      sortable: true,
      align: 'right' as const,
      headerAlign: 'right' as const,
      renderCell: ({ row }: any) => formatMoney(row.total_rp_paid),
    },
    {
      field: 'total_rp_disallowed',
      headerName: 'RP Disallowed',
      flex: 1,
      minWidth: 130,
      sortable: true,
      align: 'right' as const,
      headerAlign: 'right' as const,
      renderCell: ({ row }: any) => formatMoney(row.total_rp_disallowed),
    },
    {
      field: 'total_rp_plus_mds',
      headerName: 'RP + MDS Total',
      flex: 1,
      minWidth: 140,
      sortable: true,
      align: 'right' as const,
      headerAlign: 'right' as const,
      renderCell: ({ row }: any) => (
        <Typography variant="body2" fontWeight={700}>
          {formatMoney(row.total_rp_plus_mds)}
        </Typography>
      ),
    },
  ], []);

  // ─────────────── OWNERSHIP GROUP COLUMNS ───────────────
  const ownerGroupColumns: GridColDef[] = useMemo(() => [
    {
      field: '__expand',
      headerName: '',
      width: 40,
      sortable: false,
      disableColumnMenu: true,
      colSpan: (_value: any, row: any) => row._isDetailRow ? 99 : undefined,
      renderCell: ({ row }: any) => {
        if (row._isDetailRow) {
          const parentRow = row._parentRow;
          const m = parentRow?.metadata;
          const acuity = {
            rn: m?.ant_pct_acuity_staffed_rn ?? parentRow?.avg_acuity_rn ?? parentRow?.ant_pct_acuity_staffed_rn,
            lpn: m?.ant_pct_acuity_staffed_lpn ?? parentRow?.avg_acuity_lpn ?? parentRow?.ant_pct_acuity_staffed_lpn,
            aide: m?.ant_pct_acuity_staffed_cna ?? parentRow?.avg_acuity_aide ?? parentRow?.ant_pct_acuity_staffed_cna,
            total: m?.ant_pct_acuity_staffed_total ?? parentRow?.avg_acuity_gap ?? parentRow?.ant_pct_acuity_staffed_total,
          };
          const daysBelow = {
            rn: m?.ant_pct_days_below_rn ?? parentRow?.pct_ant_days_below_rn ?? null,
            lpn: m?.ant_pct_days_below_lpn ?? parentRow?.pct_ant_days_below_lpn ?? null,
            cna: m?.ant_pct_days_below_cna ?? parentRow?.pct_ant_days_below_cna ?? null,
          };
          const acuityHybrid = {
            rn: m?.hybrid_pct_acuity_rn ?? parentRow?.hybrid_acuity_rn ?? null,
            lpn: m?.hybrid_pct_acuity_lpn ?? parentRow?.hybrid_acuity_lpn ?? null,
            aide: m?.hybrid_pct_acuity_aide ?? parentRow?.hybrid_acuity_aide ?? null,
            total: m?.hybrid_pct_acuity_total ?? parentRow?.hybrid_acuity_total ?? null,
          };
          const daysBelowHybrid = {
            rn: m?.hybrid_pct_days_below_rn ?? parentRow?.hybrid_pct_days_below_rn ?? null,
            lpn: m?.hybrid_pct_days_below_lpn ?? parentRow?.hybrid_pct_days_below_lpn ?? null,
            cna: m?.hybrid_pct_days_below_cna ?? parentRow?.hybrid_pct_days_below_cna ?? null,
          };
          return (
            <Box sx={{ width: '100%' }}>
            <AcuityExpandPanel
              facilityAcuity={acuity}
              facilityAcuityHybrid={acuityHybrid}
              stateAcuity={{
                rn: stateContext?.avg_acuity_rn,
                lpn: stateContext?.avg_acuity_lpn,
                aide: stateContext?.avg_acuity_aide,
                total: stateContext?.avg_acuity_gap,
              }}
              stateAcuityHybrid={{
                rn: stateContext?.hybrid_acuity_rn,
                lpn: stateContext?.hybrid_acuity_lpn,
                aide: stateContext?.hybrid_acuity_aide,
                total: stateContext?.hybrid_acuity_total,
              }}
              pctDaysBelowAcuity={daysBelow}
              pctDaysBelowAcuityHybrid={daysBelowHybrid}
            />
            </Box>
          );
        }
        const rowId = row.group_id;
        const isExpanded = expandedRows.has(rowId);
        return (
          <IconButton size="small" onClick={(e: React.MouseEvent) => toggleRowExpand(rowId, e)} sx={{ p: 0.5 }}>
            {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
          </IconButton>
        );
      },
    },
    {
      field: 'primary_owner_name',
      headerName: 'Group Name',
      flex: 1.3,
      minWidth: 160,
      sortable: true,
      renderCell: ({ row }: any) => {
        const coOwners: string[] = row.co_owner_names || [];
        const coOwnerTypes: string[] = row.co_owner_types || [];
        const allNames = [row.primary_owner_name, ...coOwners];
        const displayNames = allNames.slice(0, 5);
        const remainingCount = allNames.length - 5;
        const allTypes = new Set([row.primary_type_owner, ...coOwnerTypes].filter(Boolean));
        const isMixed = allTypes.has('I') && allTypes.has('O');
        return (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 1.3, py: 0.5 }}>
            {displayNames.map((name: string, i: number) => (
              <Typography key={i} variant="body2" fontWeight={i === 0 ? 600 : 500} sx={{ wordBreak: 'break-word', color: i === 0 ? 'text.primary' : 'text.secondary', fontSize: i === 0 ? undefined : '0.8rem' }}>
                {name}
              </Typography>
            ))}
            {remainingCount > 0 && (
              <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.75rem', fontStyle: 'italic' }}>
                +{remainingCount} more
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'num_facilities',
      headerName: '# Fac',
      width: 70,
      sortable: true,
      type: 'number',
      align: 'center' as const,
      headerAlign: 'center' as const,
    },
    {
      field: 'avg_risk_score',
      headerName: 'Risk',
      width: 90,
      sortable: true,
      sortingOrder: ['desc', 'asc', null],
      align: 'center' as const,
      headerAlign: 'center' as const,
      headerClassName: 'risk-score-header',
      cellClassName: () => 'risk-score-cell',
      renderCell: ({ row }: any) => {
        const score = row.avg_risk_score;
        if (score == null) return <Typography variant="body2">-</Typography>;
        const bgColor = score >= 81 ? '#d32f2f' : score >= 61 ? '#e65100' : score >= 41 ? '#c17900' : score >= 21 ? '#558b2f' : '#2e7d32';
        return (
          <Tooltip title={`Avg risk: ${Math.round(score)}`} arrow>
            <Box sx={{ backgroundColor: bgColor, color: '#fff', borderRadius: '12px', px: 1.2, py: 0.2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 36 }}>
              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>{Math.round(score)}</Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    /* ── Anticipated Model columns hidden — state page shows Hybrid only ──
    {
      field: 'total_ant_cost_no_credit',
      headerName: 'Without Credit',
      flex: 1,
      minWidth: 120,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      headerClassName: 'section-divider-header',
      cellClassName: () => 'section-divider-cell',
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>Projected Total Understaffing</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.3 }}>Without Credit</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Sum Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020–2025</Typography>
        </Box>
      ),
      renderCell: ({ row }: any) => {
        const v = Number(row.total_ant_cost_no_credit) || 0;
        return v ? <Typography variant="body2" fontWeight={700}>{formatMoney(v)}</Typography> : <Typography variant="body2">-</Typography>;
      },
    },
    {
      field: 'total_ant_cost',
      headerName: 'With Credit',
      flex: 1,
      minWidth: 120,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>Projected Total Understaffing</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.3 }}>With Credit</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Sum Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020–2025</Typography>
        </Box>
      ),
      renderCell: ({ row }: any) => {
        const v = Math.max(0, Number(row.total_ant_cost) || 0);
        return v ? <Typography variant="body2" fontWeight={700}>{formatMoney(v)}</Typography> : <Typography variant="body2">$0</Typography>;
      },
    },
    */
    /* Hidden: % Days Failing Acuity (owner groups) — may bring back later */
    /* ── Anticipated Model columns hidden (cont.) ──
    {
      field: 'avg_acuity_gap',
      headerName: 'Total Acuity Gap',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>Total Acuity Gap</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Avg Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020–2025</Typography>
        </Box>
      ),
      valueGetter: (_value: any, row: any) => {
        const val = row.avg_acuity_gap;
        return val != null ? val - 100 : null;
      },
      renderCell: ({ row }: any) => {
        const val = row.avg_acuity_gap;
        if (val == null) return <Typography variant="body2">-</Typography>;
        const deficit = val - 100;
        const swDiff = deficit - sweetwaterAcuityTotal;
        const njDiff = deficit - njAcuityTotal;
        return (
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Typography variant="body2" fontWeight={600}
              sx={{ color: deficit < -25 ? 'error.main' : deficit < -10 ? 'warning.dark' : 'text.primary' }}>
              {deficit.toFixed(1)}%
            </Typography>
            {showBenchmarks && (
              <Box sx={{ ml: 1.5 }}>
                <Tooltip title={`vs Sweetwater (${sweetwaterAcuityTotal.toFixed(1)}%)`} arrow>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4, color: swDiff <= 0 ? 'error.main' : 'success.main', cursor: 'help' }}>
                    Sweetwater {swDiff >= 0 ? '+' : ''}{swDiff.toFixed(1)}%
                  </Typography>
                </Tooltip>
                <Tooltip title={`vs Deptford (${njAcuityTotal.toFixed(1)}%)`} arrow>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4, color: njDiff <= 0 ? 'error.main' : 'success.main', cursor: 'help' }}>
                    Deptford {njDiff >= 0 ? '+' : ''}{njDiff.toFixed(1)}%
                  </Typography>
                </Tooltip>
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      field: 'anticipated_pct_days',
      headerName: '% Days Failing Acuity (Anticipated)',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
      sortingOrder: ['desc', 'asc', null],
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>% Days Failing to Staff</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.3 }}>to Acuity</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Total Avg Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020–2025</Typography>
        </Box>
      ),
      valueGetter: (_value: any, row: any) => row.pct_ant_days_any_below ?? null,
      renderCell: ({ row }: any) => {
        const val = row.pct_ant_days_any_below;
        if (val == null) return <Typography variant="body2">-</Typography>;
        return (
          <Typography variant="body2" fontWeight={600}
            sx={{ color: Number(val) > 50 ? 'error.main' : Number(val) > 25 ? 'warning.dark' : 'text.primary' }}>
            {Number(val).toFixed(1)}%
          </Typography>
        );
      },
    },
    */
    /* Hidden: Total RN Gap (owner groups) — may bring back later */
    // ── Hybrid Model ──
    {
      field: 'hybrid_mds_cost',
      headerName: 'Hybrid Understaffing',
      flex: 1,
      minWidth: 120,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      headerClassName: 'section-divider-header',
      cellClassName: () => 'section-divider-cell',
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>Projected Total Understaffing</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.3 }}>With Credit</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Sum Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020–2025</Typography>
        </Box>
      ),
      renderCell: ({ row }: any) => {
        const v = Math.max(0, Number(row.hybrid_mds_cost) || 0);
        return v ? <Typography variant="body2" fontWeight={700}>{formatMoney(v)}</Typography> : <Typography variant="body2">$0</Typography>;
      },
    },
    {
      field: 'hybrid_pct_days_any_below',
      headerName: '% Days Failing Acuity (Hybrid)',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
      sortingOrder: ['desc', 'asc', null],
      align: 'center' as const,
      headerAlign: 'center' as const,
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>% Days Failing to Staff</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.3 }}>to Acuity</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Total Avg Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020–2025</Typography>
        </Box>
      ),
      valueGetter: (_value: any, row: any) => row.hybrid_pct_days_any_below ?? null,
      renderCell: ({ row }: any) => {
        const val = row.hybrid_pct_days_any_below;
        if (val == null) return <Typography variant="body2">-</Typography>;
        return (
          <Typography variant="body2" fontWeight={600}
            sx={{ color: Number(val) > 50 ? 'error.main' : Number(val) > 25 ? 'warning.dark' : 'text.primary' }}>
            {Number(val).toFixed(1)}%
          </Typography>
        );
      },
    },
    // ── State Minimum ──
    {
      field: 'avg_pbj_nursing_hrd',
      headerName: 'Avg HPRD',
      flex: 0.6,
      minWidth: 90,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      headerClassName: 'section-divider-header',
      cellClassName: () => 'section-divider-cell',
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>Avg HPRD</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Hours Per Resident Day</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020–2025</Typography>
        </Box>
      ),
      valueGetter: (_value: any, row: any) => row.avg_pbj_nursing_hrd ?? null,
      renderCell: ({ row }: any) => {
        const v = row.avg_pbj_nursing_hrd;
        if (v == null) return <Typography variant="body2">-</Typography>;
        return <Typography variant="body2" fontWeight={600}>{Number(v).toFixed(2)}</Typography>;
      },
    },
    {
      field: 'pct_days_below_cms_hprd',
      headerName: '% Days at State Min',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
      align: 'center' as const,
      headerAlign: 'center' as const,
      valueGetter: (_value: any, row: any) => {
        const pctBelow = row.pct_days_below_cms_hprd;
        return pctBelow != null ? 100 - pctBelow : null;
      },
      renderHeader: () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.3 }}>% Days Staffed to</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.3 }}>State Minimum</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>Total Avg Across Facilities</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 400, lineHeight: 1.3, color: 'text.disabled' }}>2020–2025</Typography>
        </Box>
      ),
      renderCell: ({ row }: any) => {
        const pctBelow = row.pct_days_below_cms_hprd;
        if (pctBelow == null) return <Typography variant="body2">-</Typography>;
        const pctMet = 100 - Number(pctBelow);
        return (
          <Typography variant="body2" fontWeight={600}
            sx={{ color: pctMet < 50 ? 'error.main' : pctMet < 75 ? 'warning.dark' : 'text.primary' }}>
            {pctMet.toFixed(1)}%
          </Typography>
        );
      },
    },
    // ── Navigate to detail page ──
    {
      field: '__navigate',
      headerName: '',
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      align: 'center' as const,
      headerClassName: 'section-divider-header',
      cellClassName: () => 'section-divider-cell',
      renderCell: ({ row }: any) => {
        const groupId = row.group_id;
        if (!groupId) return null;
        return (
          <Tooltip title="Open owner group page" arrow>
            <IconButton
              size="small"
              data-navigate
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                navigate(`/schemes/snf-understaffing/owner-group/${encodeURIComponent(groupId)}?state=${encodeURIComponent(stateFilter)}`);
              }}
              sx={{ p: 0.5, color: 'primary.main' }}
            >
              <OpenInNew sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ], [showBenchmarks, sweetwaterAcuityTotal, sweetwaterAcuityRn, njAcuityTotal, njAcuityRn, expandedRows, toggleRowExpand, navigate, stateFilter, stateContext]);

  // ─────────────── PROSECUTOR VIEW COLUMNS ───────────────
  // Simplified columns focused on HPRD comparisons to prosecution baselines
  // Order: Facility, State, MDS metrics, then Actual HPRD with comparisons
  // NOTE: field names must match backend sort field names for server-side sorting to work
  const prosecutorColumns: GridColDef<ChainRow>[] = useMemo(() => {
    // Helper to render deficit % with color coding
    const deficitCell = (val: number | null | undefined) => {
      if (val == null) return <Typography variant="body2">-</Typography>;
      const deficit = val - 100;
      return (
        <Typography variant="body2" fontWeight={600}
          sx={{ color: deficit < -25 ? 'error.main' : deficit < -10 ? 'warning.dark' : 'text.primary' }}>
          {deficit.toFixed(1)}%
        </Typography>
      );
    };

    // Helper to render % days with color
    const pctDaysCell = (val: number | null | undefined) => {
      if (val == null) return <Typography variant="body2">-</Typography>;
      return (
        <Typography variant="body2" fontWeight={600}
          sx={{ color: val > 50 ? 'error.main' : val > 25 ? 'warning.dark' : 'text.primary' }}>
          {val.toFixed(1)}%
        </Typography>
      );
    };

    // Case benchmark inline diff for acuity gap columns
    const acuityWithBenchmark = (val: number | null | undefined) => {
      if (val == null) return <Typography variant="body2">-</Typography>;
      const deficit = val - 100;
      const swDiff = deficit - sweetwaterAcuityTotal;
      const njDiff = deficit - njAcuityTotal;
      return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Typography variant="body2" fontWeight={600}
            sx={{ color: deficit < -25 ? 'error.main' : deficit < -10 ? 'warning.dark' : 'text.primary' }}>
            {deficit.toFixed(1)}%
          </Typography>
          {showBenchmarks && (
            <Box sx={{ ml: 1.5 }}>
              <Tooltip title={`vs Sweetwater (${sweetwaterAcuityTotal.toFixed(1)}%)`} arrow>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4, color: swDiff <= 0 ? 'error.main' : 'success.main', cursor: 'help' }}>
                  Sweetwater {swDiff >= 0 ? '+' : ''}{swDiff.toFixed(1)}%
                </Typography>
              </Tooltip>
              <Tooltip title={`vs Deptford (${njAcuityTotal.toFixed(1)}%)`} arrow>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4, color: njDiff <= 0 ? 'error.main' : 'success.main', cursor: 'help' }}>
                  Deptford {njDiff >= 0 ? '+' : ''}{njDiff.toFixed(1)}%
                </Typography>
              </Tooltip>
            </Box>
          )}
        </Box>
      );
    };

    const rnWithBenchmark = (val: number | null | undefined) => {
      if (val == null) return <Typography variant="body2">-</Typography>;
      const deficit = val - 100;
      const swDiff = deficit - sweetwaterAcuityRn;
      const njDiff = deficit - njAcuityRn;
      return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Typography variant="body2" fontWeight={600}
            sx={{ color: deficit < -25 ? 'error.main' : deficit < -10 ? 'warning.dark' : 'text.primary' }}>
            {deficit.toFixed(1)}%
          </Typography>
          {showBenchmarks && (
            <Box sx={{ ml: 1.5 }}>
              <Tooltip title={`vs Sweetwater RN (${sweetwaterAcuityRn.toFixed(1)}%)`} arrow>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4, color: swDiff <= 0 ? 'error.main' : 'success.main', cursor: 'help' }}>
                  Sweetwater {swDiff >= 0 ? '+' : ''}{swDiff.toFixed(1)}%
                </Typography>
              </Tooltip>
              <Tooltip title={`vs Deptford RN (${njAcuityRn.toFixed(1)}%)`} arrow>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4, color: njDiff <= 0 ? 'error.main' : 'success.main', cursor: 'help' }}>
                  Deptford {njDiff >= 0 ? '+' : ''}{njDiff.toFixed(1)}%
                </Typography>
              </Tooltip>
            </Box>
          )}
        </Box>
      );
    };

    const groupHeader = (_groupLabel: string, lines: string[], tooltip: string, _color: string) => () => (
      <Tooltip title={tooltip} arrow>
        <Box sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center' }}>
          {lines.map((line, i) => (
            <Typography key={i} sx={{
              fontSize: '0.75rem', fontWeight: i === 0 ? 600 : 400, lineHeight: 1.3,
              color: i === lines.length - 1 ? 'text.disabled' : 'text.primary',
            }}>
              {line}
            </Typography>
          ))}
        </Box>
      </Tooltip>
    );

    const subHeader = (lines: string[], _tooltip: string) => () => (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', textAlign: 'center' }}>
        {lines.map((line, i) => (
          <Typography key={i} sx={{
            fontSize: '0.75rem', fontWeight: i === 0 ? 600 : 400, lineHeight: 1.3,
            color: i === lines.length - 1 ? 'text.disabled' : 'text.primary',
          }}>
            {line}
          </Typography>
        ))}
      </Box>
    );

    return [
    // ── Expand + Facility + Risk ──
    {
      field: '__expand',
      headerName: '',
      width: 40,
      sortable: false,
      disableColumnMenu: true,
      colSpan: (_value: any, row: any) => row._isDetailRow ? 99 : undefined,
      renderCell: ({ row }: any) => {
        if (row._isDetailRow) {
          const parentRow = row._parentRow;
          const m = parentRow?.metadata;
          const acuity = {
            rn: m?.ant_pct_acuity_staffed_rn ?? parentRow?.avg_acuity_rn ?? parentRow?.ant_pct_acuity_staffed_rn,
            lpn: m?.ant_pct_acuity_staffed_lpn ?? parentRow?.avg_acuity_lpn ?? parentRow?.ant_pct_acuity_staffed_lpn,
            aide: m?.ant_pct_acuity_staffed_cna ?? parentRow?.avg_acuity_aide ?? parentRow?.ant_pct_acuity_staffed_cna,
            total: m?.ant_pct_acuity_staffed_total ?? parentRow?.avg_acuity_gap ?? parentRow?.ant_pct_acuity_staffed_total,
          };
          const daysBelow = {
            rn: m?.ant_pct_days_below_rn ?? parentRow?.pct_ant_days_below_rn ?? null,
            lpn: m?.ant_pct_days_below_lpn ?? parentRow?.pct_ant_days_below_lpn ?? null,
            cna: m?.ant_pct_days_below_cna ?? parentRow?.pct_ant_days_below_cna ?? null,
          };
          const acuityHybrid = {
            rn: m?.hybrid_pct_acuity_rn ?? parentRow?.hybrid_acuity_rn ?? null,
            lpn: m?.hybrid_pct_acuity_lpn ?? parentRow?.hybrid_acuity_lpn ?? null,
            aide: m?.hybrid_pct_acuity_aide ?? parentRow?.hybrid_acuity_aide ?? null,
            total: m?.hybrid_pct_acuity_total ?? parentRow?.hybrid_acuity_total ?? null,
          };
          const daysBelowHybrid = {
            rn: m?.hybrid_pct_days_below_rn ?? parentRow?.hybrid_pct_days_below_rn ?? null,
            lpn: m?.hybrid_pct_days_below_lpn ?? parentRow?.hybrid_pct_days_below_lpn ?? null,
            cna: m?.hybrid_pct_days_below_cna ?? parentRow?.hybrid_pct_days_below_cna ?? null,
          };
          return (
            <Box sx={{ width: '100%' }}>
              <AcuityExpandPanel
                facilityAcuity={acuity}
                facilityAcuityHybrid={acuityHybrid}
                stateAcuity={{
                  rn: stateContext?.avg_acuity_rn,
                  lpn: stateContext?.avg_acuity_lpn,
                  aide: stateContext?.avg_acuity_aide,
                  total: stateContext?.avg_acuity_gap,
                }}
                stateAcuityHybrid={{
                  rn: stateContext?.hybrid_acuity_rn,
                  lpn: stateContext?.hybrid_acuity_lpn,
                  aide: stateContext?.hybrid_acuity_aide,
                  total: stateContext?.hybrid_acuity_total,
                }}
                pctDaysBelowAcuity={daysBelow}
                pctDaysBelowAcuityHybrid={daysBelowHybrid}
              />
            </Box>
          );
        }
        const rowId = row.id;
        const isExpanded = expandedRows.has(rowId);
        return (
          <IconButton size="small" onClick={(e: React.MouseEvent) => toggleRowExpand(rowId, e)} sx={{ p: 0.5 }}>
            {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
          </IconButton>
        );
      },
    },
    {
      field: 'name',
      headerName: 'Facility',
      flex: 1.5,
      minWidth: 200,
      sortable: true,
      renderCell: ({ row }) => (
        <Box sx={{ whiteSpace: 'normal', lineHeight: 1.3, py: 0.5 }}>
          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
            {row.dba_name || stripStateFromName(row.name)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'risk_score',
      headerName: 'Risk',
      width: 90,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'risk-score-header',
      cellClassName: () => 'risk-score-cell',
      valueGetter: (_value, row) => row.metadata?.risk_score ?? null,
      renderCell: ({ row }) => {
        const score = row.metadata?.risk_score;
        const level = row.metadata?.risk_level || '';
        if (score == null) return <Typography variant="body2">-</Typography>;
        const bgColor = level === 'critical' ? '#d32f2f' : level === 'high' ? '#e65100' : level === 'elevated' ? '#c17900' : level === 'moderate' ? '#558b2f' : '#2e7d32';
        const label = level.charAt(0).toUpperCase() + level.slice(1);
        return (
          <Tooltip title={`${score} — ${label}`} arrow>
            <Box sx={{ backgroundColor: bgColor, color: '#fff', borderRadius: '12px', px: 1.2, py: 0.2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 36 }}>
              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>{Math.round(score)}</Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    /* ── Anticipated Model columns hidden — state page shows Hybrid only ──
    {
      field: 'total_mds_understaffing_cost_no_credit',
      headerName: 'Without Credit',
      flex: 1,
      minWidth: 120,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'section-divider-header',
      cellClassName: () => 'section-divider-cell',
      renderHeader: groupHeader('Anticipated Model', ['Projected Total Understaffing', 'Without Credit', '2020–2025'], 'Anticipated model: understaffing cost with NO credit for overstaffed categories.', 'rgba(21, 101, 192, 0.85)'),
      valueGetter: (_value, row) => Number(row.metadata?.total_mds_understaffing_cost_no_credit) || 0,
      renderCell: ({ row }) => {
        const v = Number(row.metadata?.total_mds_understaffing_cost_no_credit) || 0;
        return v ? <Typography variant="body2" fontWeight={700}>{formatMoney(v)}</Typography> : <Typography variant="body2">-</Typography>;
      },
    },
    {
      field: 'total_mds_understaffing_cost',
      headerName: 'With Credit',
      flex: 1,
      minWidth: 120,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: subHeader(['Projected Total Understaffing', 'With Credit', '2020–2025'], 'Anticipated model: understaffing cost WITH credit for overstaffed categories.'),
      valueGetter: (_value, row) => Math.max(0, Number(row.metadata?.total_mds_understaffing_cost) || 0),
      renderCell: ({ row }) => {
        const v = Math.max(0, Number(row.metadata?.total_mds_understaffing_cost) || 0);
        return v ? <Typography variant="body2" fontWeight={700}>{formatMoney(v)}</Typography> : <Typography variant="body2">$0</Typography>;
      },
    },
    */
    /* Hidden: % Days Failing Acuity (facilities) — may bring back later */
    /* ── Anticipated Model columns hidden (cont.) ──
    {
      field: 'ant_pct_acuity_staffed_total',
      headerName: 'Total Acuity Gap',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: subHeader(['Total Acuity Gap', '2020–2025'], 'Total acuity staffing deficit (actual - expected, as %). More negative = more understaffed.'),
      valueGetter: (_value, row) => {
        const val = row.metadata?.ant_pct_acuity_staffed_total;
        return val != null ? val - 100 : null;
      },
      renderCell: ({ row }) => acuityWithBenchmark(row.metadata?.ant_pct_acuity_staffed_total),
    },
    {
      field: 'anticipated_pct_days',
      headerName: '% Days Failing Acuity (Anticipated)',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
      sortingOrder: ['desc', 'asc', null],
      align: 'center',
      headerAlign: 'center',
      renderHeader: subHeader(['% Days Failing to Staff', 'to Acuity', '2020–2025'], 'Anticipated model: percentage of days the facility fell below the MDS acuity benchmark.'),
      valueGetter: (_value, row) => row.metadata?.pct_ant_days_any_below ?? null,
      renderCell: ({ row }) => pctDaysCell(row.metadata?.pct_ant_days_any_below),
    },
    */
    /* Hidden: Total RN Gap (facilities) — may bring back later */
    // ── Hybrid Model ──
    {
      field: 'hybrid_mds_cost',
      headerName: 'Hybrid Understaffing',
      flex: 1,
      minWidth: 120,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'section-divider-header',
      cellClassName: () => 'section-divider-cell',
      renderHeader: groupHeader('Hybrid Model', ['Projected Total Understaffing', 'With Credit', '2020–2025'], 'Hybrid model: uses CMS recommended staffing rates (higher aide requirements) with credit.', 'rgba(123, 31, 162, 0.85)'),
      valueGetter: (_value, row) => Math.max(0, Number(row.metadata?.hybrid_mds_cost) || 0),
      renderCell: ({ row }) => {
        const v = Math.max(0, Number(row.metadata?.hybrid_mds_cost) || 0);
        return v ? <Typography variant="body2" fontWeight={700}>{formatMoney(v)}</Typography> : <Typography variant="body2">$0</Typography>;
      },
    },
    {
      field: 'hybrid_pct_days_any_below',
      headerName: '% Days Failing Acuity (Hybrid)',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
      sortingOrder: ['desc', 'asc', null],
      align: 'center',
      headerAlign: 'center',
      renderHeader: subHeader(['% Days Failing to Staff', 'to Acuity', '2020–2025'], 'Hybrid model: percentage of days the facility fell below the MDS acuity benchmark.'),
      valueGetter: (_value, row) => row.metadata?.hybrid_pct_days_any_below ?? null,
      renderCell: ({ row }) => pctDaysCell(row.metadata?.hybrid_pct_days_any_below),
    },
    // ── State Minimum ──
    {
      field: 'avg_pbj_nursing_hrd',
      headerName: 'Avg HPRD',
      flex: 0.6,
      minWidth: 90,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'section-divider-header',
      cellClassName: () => 'section-divider-cell',
      renderHeader: groupHeader('State Minimum', ['Avg HPRD', 'Hours Per Resident Day', '2020–2025'], 'Average hours per resident day (PBJ nursing staff) for this facility.', 'rgba(46, 125, 50, 0.85)'),
      valueGetter: (_value, row) => row.metadata?.avg_pbj_nursing_hrd ?? null,
      renderCell: ({ row }) => {
        const v = row.metadata?.avg_pbj_nursing_hrd;
        if (v == null) return <Typography variant="body2">-</Typography>;
        return <Typography variant="body2" fontWeight={600}>{Number(v).toFixed(2)}</Typography>;
      },
    },
    {
      field: 'pct_days_below_cms_hprd',
      headerName: '% Days at State Min',
      flex: 0.8,
      minWidth: 100,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: subHeader(['% Days Staffed to', 'State Minimum', '2020–2025'], 'Percentage of days the facility met the state\'s HPRD minimum staffing requirement.'),
      valueGetter: (_value, row) => {
        const pctBelow = row.metadata?.pct_days_below_cms_hprd;
        return pctBelow != null ? 100 - pctBelow : null;
      },
      renderCell: ({ row }) => {
        const pctBelow = row.metadata?.pct_days_below_cms_hprd;
        if (pctBelow == null) return <Typography variant="body2">-</Typography>;
        const pctMet = 100 - pctBelow;
        return (
          <Typography variant="body2" fontWeight={600}
            sx={{ color: pctMet < 50 ? 'error.main' : pctMet < 75 ? 'warning.dark' : 'text.primary' }}>
            {pctMet.toFixed(1)}%
          </Typography>
        );
      },
    },
    // ── Navigate to detail page ──
    {
      field: '__navigate',
      headerName: '',
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerClassName: 'section-divider-header',
      cellClassName: () => 'section-divider-cell',
      renderCell: ({ row }) => {
        const ccn = row.metadata?.ccn || row.ccn_list?.[0];
        if (!ccn) return null;
        return (
          <Tooltip title="Open facility page" arrow>
            <IconButton
              size="small"
              data-navigate
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/schemes/snf-understaffing/facility/${ccn}`, { state: { fromState: stateFilter } });
              }}
              sx={{ p: 0.5, color: 'primary.main' }}
            >
              <OpenInNew sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];
  }, [sweetwaterAcuityTotal, sweetwaterAcuityRn, njAcuityTotal, njAcuityRn, showBenchmarks, expandedRows, toggleRowExpand, navigate, stateFilter]);

  // Use prosecutor columns (simplified view)
  const columns = prosecutorColumns;

  // Loading state
  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  // Use aggregates from the chains API (which reflects current filters) or fall back to summary
  const displayAggregates = filteredAggregates ? {
    total_chains: rowCount,
    total_facilities: filteredAggregates.total_facilities || 0,
    total_understaffed_hprd_hrs: filteredAggregates.total_understaffed_hprd_hrs || 0,
    total_understaffed_hprd_cost: filteredAggregates.total_understaffed_hprd_cost || 0,
    total_harm_citations: filteredAggregates.total_harm_citations || 0,
    total_fine_amount: filteredAggregates.total_fine_amount || 0,
  } : {
    total_chains: summary?.total_chains || 0,
    total_facilities: summary?.total_facilities || 0,
    total_understaffed_hprd_hrs: summary?.aggregates.total_understaffed_hprd_hrs || 0,
    total_understaffed_hprd_cost: summary?.aggregates.total_understaffed_hprd_cost || 0,
    total_harm_citations: summary?.aggregates.total_harm_citations || 0,
    total_fine_amount: summary?.aggregates.total_fine_amount || 0,
  };

  return (
    <Layout>
      {/* Header - matching Schemes.tsx style */}
      <Box
        mb={4}
        sx={{
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Box
          p={3}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
            transition: 'background-color 0.2s',
          }}
          onClick={() => setSchemeDetailsExpanded(!schemeDetailsExpanded)}
        >
          <Box flex={1}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Skilled Nursing Facilities: Understaffing
            </Typography>
          </Box>
          <IconButton
            size="small"
            sx={{ color: 'inherit', ml: 2 }}
          >
            {schemeDetailsExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={schemeDetailsExpanded} timeout="auto">
          <Box px={3} pb={3}>
            {/* Two-column layout: Understaffing left, Models right */}
            <Box sx={{ display: 'flex', gap: 3, mb: 2.5 }}>
              {/* Left: Understaffing + Bellwether Cases */}
              <Box sx={{ flex: 1.2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, mt: 1, fontSize: '0.95rem' }}>Understaffing</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                  Nursing homes that accept Medicare and Medicaid payments agree to staff their facilities to meet each resident's assessed clinical needs. When a facility systematically provides fewer nursing hours than its residents require—while continuing to bill the government for full reimbursement—it is unlawfully collecting money it is not entitled to, sometimes rising to the level of an actionable fraud case.
                  {'\n\n'}
                  EliotNest identifies understaffing by comparing a facility's actual staffing levels (from CMS Payroll-Based Journal data) against the staffing levels that its residents required (from MDS patient assessments), for every facility in the country, every day, from 2020 to the present.
                </Typography>

                <Typography variant="body2" sx={{ fontWeight: 700, mt: 2.5, mb: 0.5, fontSize: '0.95rem' }}>Bellwether Cases</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1.6, display: 'block', mb: 1 }}>
                  <strong><em>State of New Jersey v. Deptford Center and Hammonton Center</em> (January 2026)</strong><br />New Jersey sued the operators of two nursing homes for intentional understaffing and diversion of Medicaid funds through nine related-party entities. The suit names 34 defendants and seeks <strong>$124 million</strong> in repayment.
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1.6, display: 'block' }}>
                  <strong><em>People of the State of California v. Sweetwater Care et al.</em> (June 2025)</strong><br />California sued a chain of 17 nursing homes for over 25,000 staffing-law violations between 2020 and 2024, alleging that operators extracted $31 million in profits rather than investing in required staffing. Potential penalties: up to <strong>$2,500 per violation</strong> under Bus. &amp; Prof. Code § 17200 plus <strong>$100,000 per day</strong> of staffing shortfalls under H&amp;SC § 1276.65—total exposure potentially exceeding $62 million on the per-violation count alone.
                </Typography>
              </Box>

              {/* Right: Models */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, borderLeft: '1px solid rgba(255,255,255,0.15)', pl: 3 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'rgba(144, 202, 249, 1)', mb: 0.25, mt: 1, fontSize: '0.95rem' }}>Anticipated Model</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.95, lineHeight: 1.5, display: 'block' }}>
                    The Anticipated Model uses the CMS Staff Time Measurement study—the government's own empirical benchmark of how many RN, LPN, and CNA minutes each type of resident requires—to calculate expected staffing for each facility based on its actual resident population. The gap between expected and actual hours is the understaffing deficit. We calculate the dollar amount by using each facility's own average salary for its specific staffing group.
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.95, lineHeight: 1.5, display: 'block', mt: 1.5 }}>
                    <strong>With Staffing Credit</strong> allows overstaffing in one nursing category (e.g., excess LPN hours) to offset shortfalls in another (e.g., insufficient RN hours). This gives facilities some credit for—most commonly—overstaffing LPNs to account for nursing shortages, despite those roles providing completely different types of care.
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.95, lineHeight: 1.5, display: 'block', mt: 1.5 }}>
                    <strong>Without Staffing Credit</strong> counts only the categories where the facility fell short, with no offset. This reflects the position that each nursing category serves a distinct clinical function—a surplus of aides cannot substitute for a shortage of registered nurses.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'rgba(206, 147, 216, 1)', mb: 0.25, fontSize: '0.95rem' }}>Hybrid Model</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.95, lineHeight: 1.5, display: 'block' }}>
                    The Hybrid Model applies the same methodology as the Anticipated Model but uses a more demanding benchmark. Instead of relying solely on the CMS Staff Time Measurement study, it incorporates upward staffing adjustments recommended by Harrington et al., "Appropriate Nurse Staffing Levels for U.S. Nursing Homes," <em>Health Services Insights</em>, Vol. 13 (2020).
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff', mb: 0.25, fontSize: '0.95rem' }}>Percentage of Days</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.95, lineHeight: 1.5, display: 'block' }}>
                    The Percent Days Model asks a binary question for each day: did the facility meet the applicable staffing benchmark, or did it not? Rather than calculating a continuous gap, it reports the percentage of days during the analysis period on which the facility was understaffed.
                    {' '}This is a straightforward metric for enforcement: the facility was understaffed on X% of days over Y years. On those days, regulators can seek to recover Medicaid payments under a breach of contract theory.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'rgba(165, 214, 167, 1)', mb: 0.25, fontSize: '0.95rem' }}>State Minimum</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.95, lineHeight: 1.5, display: 'block' }}>
                    In states with numeric Hours Per Resident Day (HPRD) requirements, this model asks whether the facility met the state's minimum staffing standard on a given day.
                    {' '}This model is limited to those states that impose quantitative minimums, but where applicable it provides a straightforward basis for liability because the standard is an explicit number set by statute or regulation.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* State Context Banner */}
      {stateContext && stateFilter && (() => {
        const anticipatedCost = Math.max(0, stateContext.total_ant_cost || 0);
        const anticipatedCostNoCredit = Math.max(0, stateContext.total_ant_cost_no_credit || 0);


        const metricLabelSx = { fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 } as const;
        const subLabelSx = { fontSize: '0.65rem' } as const;
        const metricBoxSx = { flexShrink: 0, cursor: 'help', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' } as const;

        return (
        <Paper elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
          {/* State name + staffing reqs + back to national */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Typography fontWeight={800} fontSize="1.4rem" color="text.primary">{STATE_NAMES[stateFilter] || stateFilter}</Typography>
            {STATE_STAFFING_REQS[stateFilter] && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                <Box onClick={() => setStaffingReqsOpen(true)} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', '&:hover': { '& .MuiTypography-root': { textDecoration: 'underline' } } }}>
                  <InfoIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>State Staffing Requirements</Typography>
                </Box>
              </>
            )}
            <Box sx={{ flex: 1 }} />
            <Typography
              variant="caption"
              sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => navigate('/schemes/snf-understaffing')}
            >
              ← National Overview
            </Typography>
          </Box>

          {/* Staffing Requirements Dialog — dynamic per state */}
          {STATE_STAFFING_REQS[stateFilter] && (
          <Dialog open={staffingReqsOpen} onClose={() => setStaffingReqsOpen(false)} maxWidth={false}
            PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', maxWidth: 1050 } }}
          >
            {/* Accent bar */}
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #1565c0, #7b1fa2, #2e7d32)' }} />
            <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
              {STATE_NAMES[stateFilter] || stateFilter} — Staffing Requirements
            </DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
              {(() => {
                const reqs = STATE_STAFFING_REQS[stateFilter];
                if (!reqs) return null;
                return (
                <Box sx={{ display: 'flex', gap: 2.5 }}>
                  {/* Minimum Staffing Floor */}
                  <Paper variant="outlined" sx={{ flex: 1, p: 2.5, borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Box sx={{ width: 4, height: 20, bgcolor: '#1565c0', borderRadius: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Minimum Staffing Floor</Typography>
                    </Box>

                    {/* Key number callout */}
                    <Box sx={{ bgcolor: '#e3f2fd', borderRadius: 1.5, px: 2, py: 1, mb: 1.5, textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#1565c0' }}>{reqs.hprd.toFixed(2)}</Typography>
                      <Typography variant="caption" color="text.secondary">hours per resident day</Typography>
                    </Box>

                    {/* Shift requirements (if state has them, e.g. MI) */}
                    {reqs.shiftRequirements && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Shift Requirements:</Typography>
                        <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.5 } }}>
                          {reqs.shiftRequirements.map((sr, i) => (
                            <li key={i}><Typography variant="body2"><strong>{sr.shift}:</strong> {sr.requirement}</Typography></li>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Sub-requirements — HPRD and CNA/LPN only */}
                    {(reqs.cna || reqs.licensedNurseHppd) && (
                      <Box sx={{ mb: 1 }}>
                        {reqs.cna && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>CNA minimum: <strong>{reqs.cna.toFixed(1)} HPRD</strong></Typography>
                        )}
                        {reqs.licensedNurseHppd && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>Licensed nurse (RN+LPN): <strong>{reqs.licensedNurseHppd} HPRD</strong></Typography>
                        )}
                      </Box>
                    )}

                    {reqs.citation ? (
                      <Box sx={{ mt: 'auto', pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                          {reqs.citation}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ mt: 'auto' }} />
                    )}
                  </Paper>

                  {/* Acuity-Based Staffing Sufficiency */}
                  <Paper variant="outlined" sx={{ flex: 1, p: 2.5, borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Box sx={{ width: 4, height: 20, bgcolor: '#2e7d32', borderRadius: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Acuity-Based Sufficiency</Typography>
                    </Box>

                    {reqs.acuityText ? (
                      <>
                        <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                          {reqs.acuityText}
                        </Typography>
                        {reqs.acuityDetail && (
                          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', lineHeight: 1.6, bgcolor: 'grey.50', borderRadius: 1, px: 1.5, py: 1 }}>
                            {reqs.acuityDetail}
                          </Typography>
                        )}
                        <Box sx={{ mt: 'auto', pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                            {reqs.acuityCitation}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                          State-specific acuity requirements coming soon.
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Box>
                );
              })()}
            </DialogContent>
          </Dialog>
          )}

          <Divider sx={{ mb: 1.5 }} />

          {/* Metrics row */}
          <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2, justifyContent: 'center' }}>
            {/* Facilities */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ ...metricLabelSx, textAlign: 'center' }}>Facilities</Typography>
              <Typography fontWeight={700} fontSize="1.25rem" sx={{ textAlign: 'center' }}>{formatNumber(stateContext.num_facilities)}</Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* Avg HPRD */}
            <Tooltip title="Average hours per resident day across all facilities in this state, based on PBJ staffing data." arrow>
              <Box sx={{ flex: 1, textAlign: 'center', justifyContent: 'center', alignItems: 'center', display: 'flex', flexDirection: 'column', cursor: 'help' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={metricLabelSx}>Avg HPRD</Typography>
                <Typography fontWeight={700} fontSize="1.25rem">{formatNumber(stateContext.avg_hprd, 2)}</Typography>
              </Box>
            </Tooltip>

            {/* Projected Understaffing Cost card removed
            <Divider orientation="vertical" flexItem />

            Projected Understaffing Cost (Hybrid)
            <Tooltip title="Estimated labor cost of understaffing vs MDS acuity-based requirements using the Hybrid model, with overstaffing credit (per-year floored at $0)." arrow>
            <Box sx={{ ...metricBoxSx, flex: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={metricLabelSx}>Projected Understaffing Cost (Hybrid, With Credit)</Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', display: 'block' }}>2020–2025</Typography>
              <Typography fontWeight={700} fontSize="1.1rem" sx={{ mt: 0.5 }}>{formatMoney(Math.max(0, stateContext.hybrid_mds_cost || 0))}</Typography>
            </Box>
            </Tooltip>
            */}

            <Divider orientation="vertical" flexItem />

            {/* % Days Fail to Staff to Acuity (Hybrid) */}
            <Tooltip title="Average percentage of days facilities fail to staff to MDS acuity requirements (Hybrid model)." arrow>
            <Box sx={{ ...metricBoxSx, flex: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={metricLabelSx}>% Days Fail to Staff to Acuity (Hybrid)</Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', display: 'block' }}>2020–2025</Typography>
              <Typography fontWeight={700} fontSize="1.1rem" sx={{ mt: 0.5 }}>{formatPercent(stateContext.hybrid_pct_days_any_below)}</Typography>
            </Box>
            </Tooltip>

            <Divider orientation="vertical" flexItem />

            {/* % Days Fail to Staff to State Minimum */}
            <Tooltip title={`Average percentage of days facilities fall below the ${({'MA': 'state', 'WA': 'state', 'NJ': 'state', 'VT': 'state', 'MI': 'state', 'WY': 'state'} as Record<string, string>)[stateFilter] || 'CMS'} minimum staffing threshold (${({'MA': '3.58', 'WA': '3.40', 'NJ': '3.40', 'VT': '3.00', 'MI': '2.25', 'WY': '1.50'} as Record<string, string>)[stateFilter] || '3.50'} HPRD).`} arrow>
            <Box sx={{ ...metricBoxSx, flex: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={metricLabelSx}>% Days Fail to Staff to State Minimum</Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', display: 'block' }}>2020–2025</Typography>
              <Box sx={{ mt: 0.5 }}>
                <Typography fontWeight={700} fontSize="1.1rem">{formatPercent(stateContext.pct_days_below_cms_hprd)}</Typography>
              </Box>
            </Box>
            </Tooltip>

          </Box>

        </Paper>
        );
      })()}

      {/* Search and Filters Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          pb: 0,
          mb: 4,
          backgroundColor: 'white',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
            <TextField
              size="small"
              placeholder="Search by name or CCN..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPaginationModel(prev => ({ ...prev, page: 0 }));
                  fetchTargets();
                }
              }}
              sx={{
                minWidth: 250,
                backgroundColor: 'background.default',
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            {/* State filter hidden — will bring back soon
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Filter by State</InputLabel>
              <Select
                value={stateFilter}
                label="Filter by State"
                onChange={(e) => {
                  const newState = e.target.value;
                  setStateFilter(newState);
                  setPaginationModel(prev => ({ ...prev, page: 0 }));
                  if (newState) {
                    navigate(`/schemes/snf-understaffing/state/${newState}`, { replace: true });
                  }
                }}
                sx={{ backgroundColor: 'background.default' }}
              >
                <MenuItem value=""><em>All States</em></MenuItem>
                {availableStates.map((state) => (
                  <MenuItem key={state} value={state}>{state}</MenuItem>
                ))}
              </Select>
            </FormControl>
            */}
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>MDS Acuity Filter</InputLabel>
              <Select
                value={acuityFilter}
                label="MDS Acuity Filter"
                onChange={(e) => {
                  setAcuityFilter(e.target.value);
                  setPaginationModel(prev => ({ ...prev, page: 0 }));
                }}
                sx={{ backgroundColor: 'background.default' }}
              >
                <MenuItem value=""><em>{showOwnerGroups ? 'All Groups' : 'All Facilities'}</em></MenuItem>
                <MenuItem value="below_sweetwater">Worse than Sweetwater ({sweetwaterAcuityTotal.toFixed(1)}%)</MenuItem>
                <MenuItem value="below_nj">Worse than Deptford ({njAcuityTotal.toFixed(1)}%)</MenuItem>
              </Select>
            </FormControl>
            {/* Case Benchmarks toggle hidden — showBenchmarks stays false
            <FormControlLabel
              control={
                <Switch
                  checked={showBenchmarks}
                  onChange={(e) => setShowBenchmarks(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2" color="text.secondary">Case Benchmarks</Typography>}
              sx={{ ml: 1 }}
            />
            */}
            {/* State minimum requirements display - hidden for now */}
            {/* {stateFilter && STATE_STAFFING_REQS[stateFilter] && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.25 }}>
                    {stateFilter} State Minimum Requirements
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2, '& li': { fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.4 } }}>
                    {STATE_STAFFING_REQS[stateFilter].hprd && (
                      <li>{STATE_STAFFING_REQS[stateFilter].hprd} Hours Per Resident Day (HPRD)</li>
                    )}
                    {STATE_STAFFING_REQS[stateFilter].cna && (
                      <li>{STATE_STAFFING_REQS[stateFilter].cna} CNA Hours Per Patient Day</li>
                    )}
                    {STATE_STAFFING_REQS[stateFilter].rnHoursPerDay && (
                      <li>{STATE_STAFFING_REQS[stateFilter].rnHoursPerDay} RN Hours Per Day</li>
                    )}
                    {STATE_STAFFING_REQS[stateFilter].rnHppd && (
                      <li>{STATE_STAFFING_REQS[stateFilter].rnHppd} RN Hours Per Patient Day</li>
                    )}
                    {STATE_STAFFING_REQS[stateFilter].licensedNurseHppd && (
                      <li>{STATE_STAFFING_REQS[stateFilter].licensedNurseHppd} Licensed Nurse (RN+LPN) Hours Per Patient Day</li>
                    )}
                  </Box>
                </Box>
              </>
            )} */}
            <Box flex={1} />
            <ToggleButtonGroup
              value={showOwnerGroups ? 'ownerGroups' : 'facilities'}
              exclusive
              onChange={(_e, val) => {
                if (val) {
                  const switching = val === 'ownerGroups';
                  setShowOwnerGroups(switching);
                  setPaginationModel(prev => ({ ...prev, page: 0 }));
                  setSortModel([{
                    field: switching ? 'hybrid_mds_cost' : 'risk_score',
                    sort: 'desc',
                  }]);
                  const newParams = new URLSearchParams(searchParams);
                  if (switching) {
                    newParams.set('view', 'ownerGroups');
                  } else {
                    newParams.delete('view');
                  }
                  setSearchParams(newParams, { replace: true });
                }
              }}
              size="small"
            >
              <ToggleButton value="ownerGroups" sx={{
                '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } },
              }}>Ownership Groups</ToggleButton>
              <ToggleButton value="facilities" sx={{
                '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } },
              }}>Facilities</ToggleButton>
            </ToggleButtonGroup>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 0.5, pt: 0.5, display: 'block', mt: -0.5 }}>
          * Based on CMS PBJ staffing and MDS acuity data from 2020–2025
        </Typography>

        {/* Facility DataGrid */}
        <Box sx={{ px: 1, pb: 2, pt: 1, minHeight: 650, height: '85vh' }}>
        <DataGrid
          key={`grid-${showOwnerGroups ? 'og' : 'fac'}-${showBenchmarks ? 'bm' : 'nobm'}`}
          rows={showOwnerGroups ? processedOwnerGroupRows : processedFacilityRows}
          columns={showOwnerGroups ? ownerGroupColumns : columns}
          loading={showOwnerGroups ? ownerGroupsLoading : chainsLoading}
          getRowId={(row) => showOwnerGroups
            ? row.group_id
            : row.id}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={showOwnerGroups ? ownerGroupsRowCount : rowCount}
          pageSizeOptions={[10, 25, 50, 100]}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 140}
          columnHeaderHeight={80}
          columnGroupHeaderHeight={32}
          getRowClassName={(params) => params.row._isDetailRow ? 'detail-panel-row' : ''}
          onRowClick={(params, event) => {
            // Don't toggle detail rows or link/button clicks
            if (params.row._isDetailRow) return;
            const target = event.target as HTMLElement;
            if (target.closest('a, button, [data-navigate]')) return;
            const rowId = showOwnerGroups ? params.row.group_id : params.row.id;
            if (rowId) {
              setExpandedRows(prev => {
                const next = new Set(prev);
                if (next.has(rowId)) next.delete(rowId);
                else next.add(rowId);
                return next;
              });
            }
          }}
          columnGroupingModel={(() => {
            const renderGroup = (label: string, color: string) => () => (
              <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase', color }}>
                {label}
              </Typography>
            );
            if (showOwnerGroups) {
              return [
                { groupId: 'risk_group', headerName: '', headerClassName: 'risk-score-group', children: [{ field: 'avg_risk_score' }], renderHeaderGroup: () => null },
                // { groupId: 'anticipated_model', headerName: 'Anticipated Model', headerClassName: 'group-divider-left', children: [{ field: 'total_ant_cost_no_credit' }, { field: 'total_ant_cost' }, { field: 'avg_acuity_gap' }, { field: 'anticipated_pct_days' }], renderHeaderGroup: renderGroup('Anticipated Model', 'rgba(21, 101, 192, 0.85)') },
                { groupId: 'hybrid_model', headerName: 'Hybrid Model', headerClassName: 'group-divider-left', children: [{ field: 'hybrid_mds_cost' }, { field: 'hybrid_pct_days_any_below' }], renderHeaderGroup: renderGroup('Hybrid Model', 'rgba(123, 31, 162, 0.85)') },
                { groupId: 'state_minimum', headerName: 'State Minimum', headerClassName: 'group-divider-left', children: [{ field: 'avg_pbj_nursing_hrd' }, { field: 'pct_days_below_cms_hprd' }], renderHeaderGroup: renderGroup('State Minimum', 'rgba(46, 125, 50, 0.85)') },
                { groupId: 'nav_group', headerName: '', headerClassName: 'group-divider-left', children: [{ field: '__navigate' }], renderHeaderGroup: () => null },
              ] as GridColumnGroupingModel;
            }
            return [
              { groupId: 'risk_group', headerName: '', headerClassName: 'risk-score-group', children: [{ field: 'risk_score' }], renderHeaderGroup: () => null },
              // { groupId: 'anticipated_model', headerName: 'Anticipated Model', headerClassName: 'group-divider-left', children: [{ field: 'total_mds_understaffing_cost_no_credit' }, { field: 'total_mds_understaffing_cost' }, { field: 'ant_pct_acuity_staffed_total' }, { field: 'anticipated_pct_days' }], renderHeaderGroup: renderGroup('Anticipated Model', 'rgba(21, 101, 192, 0.85)') },
              { groupId: 'hybrid_model', headerName: 'Hybrid Model', headerClassName: 'group-divider-left', children: [{ field: 'hybrid_mds_cost' }, { field: 'hybrid_pct_days_any_below' }], renderHeaderGroup: renderGroup('Hybrid Model', 'rgba(123, 31, 162, 0.85)') },
              { groupId: 'state_minimum', headerName: 'State Minimum', headerClassName: 'group-divider-left', children: [{ field: 'avg_pbj_nursing_hrd' }, { field: 'pct_days_below_cms_hprd' }], renderHeaderGroup: renderGroup('State Minimum', 'rgba(46, 125, 50, 0.85)') },
              { groupId: 'nav_group', headerName: '', headerClassName: 'group-divider-left', children: [{ field: '__navigate' }], renderHeaderGroup: () => null },
            ] as GridColumnGroupingModel;
          })()}
          sx={dataGridStyles}
        />
        </Box>
      </Paper>

      {/* ───── Section: Data Sources ───── */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Data Sources
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Dataset</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>View</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>Minimum Data Set (MDS)</TableCell>
                  <TableCell>Daily census of anonymized patient acuity assessments for every nursing facility in the US. Used to calculate expected staffing levels based on actual patient care needs.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://www.cms.gov/medicare/quality/nursing-home-improvement/minimum-data-sets-swing-bed-providers" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>Payroll Based Journal Daily Nurse Staffing</TableCell>
                  <TableCell>Provides daily hours for each staff type (nursing) per facility.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://data.cms.gov/quality-of-care/payroll-based-journal-daily-nurse-staffing" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>Payroll Based Journal Daily Non-Nurse Staffing</TableCell>
                  <TableCell>Provides daily hours for each staff type (non-nursing) per facility.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://data.cms.gov/quality-of-care/payroll-based-journal-daily-non-nurse-staffing" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>Skilled Nursing Facility Enrollments</TableCell>
                  <TableCell>Information on Skilled Nursing Facilities currently enrolled in Medicare. Provides names, addresses, and other related data for each SNF.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://data.cms.gov/provider-characteristics/hospitals-and-other-facilities/skilled-nursing-facility-enrollments" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>Skilled Nursing Facility All Owners</TableCell>
                  <TableCell>Provides information on all owners of SNFs currently enrolled in Medicare.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://data.cms.gov/provider-characteristics/hospitals-and-other-facilities/skilled-nursing-facility-all-owners" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>SNF Ownership Change</TableCell>
                  <TableCell>List of every ownership change for all SNFs.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://data.cms.gov/provider-characteristics/hospitals-and-other-facilities/skilled-nursing-facility-change-of-ownership" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'medium' }}>SNF Cost Reports (Form 2540-10)</TableCell>
                  <TableCell>Skilled nursing facility cost reports from HCRIS, providing financial data for Medicare reimbursement analysis.</TableCell>
                  <TableCell>
                    <Button size="small" variant="text" href="https://www.cms.gov/data-research/statistics-trends-and-reports/cost-reports/skilled-nursing-facility-2540-2010-form" target="_blank">
                      Source
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

    </Layout>
  );
}
